import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import {
  Phone,
  MessageCircle,
  ChevronRight,
  ChevronLeft,
  PlayCircle,
  DollarSign,
  Rocket,
  Menu,
  X,
  Headset,
  Activity,
  FileBarChart,
  BriefcaseBusiness,
  Sparkles,
  CheckCircle2,
  BookOpen,
  Award,
  Users,
  TrendingUp,
  Gift,
  Truck,
  ShieldCheck,
} from "lucide-react";

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M12 2c1.2 2.5 3.2 3.7 5.5 3.8v3c-1.5-.1-2.8-.5-4-1.2v6.6c0 3.4-2.6 5.8-6 5.8S2 17.6 2 14.2 4.6 8.4 8 8.4c.5 0 1 .1 1.5.2v3.1c-.4-.2-.9-.3-1.5-.3-1.7 0-3 1.3-3 3s1.3 3 3 3 3-1.3 3-3V2h1z" />
  </svg>
);

const brandName = "WOLF MEDIA";
const topMiniBrand = "";
const topBadge = "TIKTOK RESMİ PARTNERİ";

const tiktokUrl = "https://www.tiktok.com/t/ZMkc4Qj7J/";
const whatsappNumber = "+905320511256";
const phoneNumber = "+905320511256";
const phoneDisplay = "+90 532 051 12 56";
const contactEmail = "bilgi@wolfmediaajans.com";

const API_FEATURED_PUBLISHERS = "/api/featured-publisher-list.php";
const API_VISITOR_TRACK = "/api/visitor-track.php";
const API_VISITOR_STATS = "/api/visitor-stats.php";
const IMAGE_VERSION = "20260527_05";

const navItems = [
  { label: "Hakkımızda", href: "#anasayfa" },
  { label: "Yayıncılarımız", href: "#yayincilarimiz" },
  { label: "Hizmetler", href: "#hizmetler" },
  { label: "Ödüller", href: "#oduller" },
  { label: "Hesaplayıcı", href: "#hesaplayici" },
  { label: "SSS", href: "#sss" },
  { label: "İletişim", href: "#iletisim" },
];

const heroDesc =
  "TikTok'ta yayıncı olmak ya da kazancını büyütmek istiyorsan doğru yerdesin. Wolf Media Ajans'ın stratejik desteğiyle yükselişini hızlandırabilir, kazancını zirveye taşıyabilirsin.";

const trustItems = [
  { title: "7/24 aktif destek sistemi", icon: Headset },
  { title: "Gerçek zamanlı performans takibi", icon: Activity },
  { title: "Kullanıcıya özel rapor altyapısı", icon: FileBarChart },
  { title: "Wolf Media Akademi", icon: BriefcaseBusiness },
];

const featureCards = [
  {
    id: "yeni-baslayanlar",
    title: "TikTok'ta Yeniyseniz İzlemeniz Gereken Stratejiler",
    desc: "Yeni başlayan yayıncılar için doğru başlangıç adımları, içerik dili ve görünürlük stratejileriyle süreci sağlam kuruyoruz.",
    icon: PlayCircle,
    image: "/card-strateji.jpg",
    accent: "from-yellow-500/20 to-transparent",
    guidePoints: [
      "İlk 5 saniyede izleyiciyi ekranda tutan dikkat çekici kanca (hook) teknikleri.",
      "Yayın başlığı ve etiketlerin TikTok algoritmasıyla uyumlu doğru seçimi.",
      "Doğru ışık açısı, net mikrofon sesi ve profesyonel yayın alanı düzeni.",
      "İlk 30 günde düzenli yayın takvimi ile algoritma güven puanı oluşturma."
    ]
  },
  {
    id: "one-cikma",
    title: "TikTok'ta Öne Çıkmanın Formülleri",
    desc: "İzlenmeyi ve etkileşimi artıran içerik akışını, doğru saatleri ve hızlı yükseliş fırsatlarını birlikte planlıyoruz.",
    icon: Rocket,
    image: "/card-one-cikma.jpg",
    accent: "from-yellow-400/15 to-transparent",
    guidePoints: [
      "Hedef kitlenizin en aktif olduğu saatlerde yayına başlayarak 'Keşfet'e düşme.",
      "PK savaşları ve ajans içi yayıncı eşleşmeleriyle yeni izleyici kitleleri kazanma.",
      "Yayın içi mini anketler, meydan okumalar ve sohbeti canlı tutma taktikleri.",
      "İzleyicileri hediye göndermeye ve destek olmaya teşvik eden yayın dili."
    ]
  },
  {
    id: "surdurulebilir-kazanc",
    title: "TikTok'ta Sürdürülebilir ve Artan Kazanç",
    desc: "Sadece anlık yükseliş değil, düzenli gelir ve istikrarlı performans için uzun vadeli yapı kuruyoruz.",
    icon: DollarSign,
    image: "/card-kazanc.jpg",
    accent: "from-yellow-300/15 to-transparent",
    guidePoints: [
      "Haftalık ve aylık elmas hedefleri belirleyerek adım adım kademe yükseltme.",
      "Ana destekçilerinizle (VIP donators) saygılı ve kalıcı bir topluluk bağı kurma.",
      "Wolf Media Ajans'ın aylık ekipman, telefon ve nakit ödül fonlarından faydalanma.",
      "Gelir dalgalanmalarını önleyen uzun vadeli profesyonel yayıncı planlaması."
    ]
  },
];

function getPublisherProfileUrl(publisher) {
  if (publisher.tiktokUrl) {
    return publisher.tiktokUrl;
  }

  const username = String(publisher.about || "")
    .trim()
    .replace(/^@/, "")
    .replace(/\s+/g, "");

  if (username) {
    return `https://www.tiktok.com/@${username}`;
  }

  return tiktokUrl;
}

function openPublisherProfile(publisher) {
  window.open(getPublisherProfileUrl(publisher), "_blank", "noopener,noreferrer");
}

function getPublisherTransform(offset) {
  if (offset === -2) {
    return "translate(calc(-50% - 440px), -50%) perspective(1000px) rotateY(12deg) scale(0.74)";
  }
  if (offset === -1) {
    return "translate(calc(-50% - 240px), -50%) perspective(1000px) rotateY(6deg) scale(0.86)";
  }
  if (offset === 0) {
    return "translate(-50%, -50%) perspective(1000px) rotateY(0deg) scale(1)";
  }
  if (offset === 1) {
    return "translate(calc(-50% + 240px), -50%) perspective(1000px) rotateY(-6deg) scale(0.86)";
  }
  if (offset === 2) {
    return "translate(calc(-50% + 440px), -50%) perspective(1000px) rotateY(-12deg) scale(0.74)";
  }
  return "translate(-50%, -50%)";
}

function getPublisherVisibilityClass(offset) {
  if (offset === -2 || offset === 2) {
    return "hidden xl:block opacity-60 z-10";
  }
  if (offset === -1 || offset === 1) {
    return "hidden sm:block opacity-85 z-20";
  }
  if (offset === 0) {
    return "opacity-100 z-30";
  }
  return "hidden";
}

function formatPublisherBadgeText(publisher, offset) {
  if (!publisher) return "🔥 Wolf Creator";

  // 1. Gerçek takipçi sayısı varsa (TikTok'tan çekilen veya veritabanında olan)
  const rawFollowers = publisher.followerCount ?? publisher.followers ?? publisher.follower_count ?? publisher.fans;
  if (rawFollowers !== undefined && rawFollowers !== null && rawFollowers !== "" && rawFollowers !== "0" && rawFollowers !== 0) {
    const num = Number(String(rawFollowers).replace(/[^0-9.]/g, ""));
    if (!Number.isNaN(num) && num > 0) {
      if (num >= 1000000) return `👤 ${(num / 1000000).toFixed(1).replace(".0", "")}M`;
      if (num >= 1000) return `👤 ${(num / 1000).toFixed(1).replace(".0", "")}K`;
      return `👤 ${num.toLocaleString("tr-TR")}`;
    }
    if (typeof rawFollowers === "string" && rawFollowers.trim() && rawFollowers.trim() !== "0") {
      return `👤 ${rawFollowers.trim()}`;
    }
  }

  // 2. Kullanıcı adı (@username) varsa doğrudan gerçek TikTok kullanıcı adını göster
  const rawUser = String(publisher.username || publisher.about || "").replace(/^@/, "").trim();
  if (rawUser) {
    return `@${rawUser}`;
  }

  // 3. Sıralama / Statü rozeti
  const rankBadges = ["⭐ VIP Partner", "🔥 Top Performer", "✨ Rising Star", "💎 Pro Creator", "👑 Elite Star"];
  return rankBadges[Math.abs(offset) % rankBadges.length];
}

function getPublisherMeta(offset) {
  const meta = [
    {
      badge: "VIP",
      role: "TOP PERFORMER",
      reach: "12.8M+",
      fans: "VIP",
      score: "9.8",
      bio: "Yüksek etkileşim, güçlü topluluk ve istikrarlı performansıyla markalara maksimum değer katar.",
    },
    {
      badge: "OFFICIAL PARTNER",
      role: "OFFICIAL PARTNER",
      reach: "7.2M+",
      fans: "PRO",
      score: "9.4",
      bio: "Güçlü yayın disiplini ve yüksek etkileşim oranıyla öne çıkan yayıncılarımızdan.",
    },
    {
      badge: "TOP PERFORMER",
      role: "TOP PERFORMER",
      reach: "6.1M+",
      fans: "STAR",
      score: "9.5",
      bio: "İstikrarlı yayın akışı ve güçlü izleyici bağı ile fark yaratıyor.",
    },
    {
      badge: "OFFICIAL PARTNER",
      role: "PREMIUM CREATOR",
      reach: "5.3M+",
      fans: "VIP",
      score: "9.3",
      bio: "Marka değeri ve yayın kalitesiyle güçlü bir yayıncı profili oluşturuyor.",
    },
    {
      badge: "RISING STAR",
      role: "RISING STAR",
      reach: "4.7M+",
      fans: "RISING",
      score: "9.2",
      bio: "Yükselen performansı ve enerjik yayın diliyle dikkat çekiyor.",
    },
  ];

  if (offset === 0) return meta[0];
  if (offset === -2) return meta[1];
  if (offset === -1) return meta[2];
  if (offset === 1) return meta[3];
  if (offset === 2) return meta[4];

  return meta[0];
}

const defaultFeaturedPublishers = [
  { id: 1, name: "GÖKÇE", image: "/yayinci1.jpg", status: "Aktif", role: "VIP Star", about: "@gokceofficial", bio: "TikTok Türkiye'nin sevilen eğlence ve sohbet yayıncısı. Wolf Media ile aylık 2.5M+ elmas başarısı." },
  { id: 2, name: "CANBERK", image: "/yayinci2.jpg", status: "Aktif", role: "Pro Yayıncı", about: "@canberk_live", bio: "PK turnuvalarında yenilgisiz şampiyonluk serileri ve canlı enerjisiyle kitleleri büyülüyor." },
  { id: 3, name: "ELİF YILMAZ", image: "/yayinci3.jpg", status: "Aktif", role: "Ajans Yıldızı", about: "@elifyilmaz", bio: "Samimi sohbeti ve yüksek etkileşimiyle TikTok'ta hızla zirveye yükselen yıldızımız." },
  { id: 4, name: "BURAK", image: "/yayinci4.jpg", status: "Aktif", role: "Top PK Oyuncusu", about: "@burakgaming", bio: "Oyun ve interaktif yayınlarda lider, 500K+ takipçili dev topluluğuyla fark yaratıyor." },
  { id: 5, name: "SEDA", image: "/yayinci5.jpg", status: "Aktif", role: "VIP Yayıncı", about: "@sedalive", bio: "Özgün müzik ve eğlence konseptleriyle her akşam binlerce canlı izleyiciye ulaşıyor." },
];

function PublisherStageGlow() {
  const { isDark } = useTheme();
  const particles = [
    { left: "15%", bottom: "38%", delay: "0s", size: 4 },
    { left: "24%", bottom: "55%", delay: ".7s", size: 5 },
    { left: "35%", bottom: "68%", delay: "1.4s", size: 6 },
    { left: "50%", bottom: "75%", delay: "2.1s", size: 5 },
    { left: "65%", bottom: "66%", delay: ".35s", size: 6 },
    { left: "76%", bottom: "52%", delay: "1.05s", size: 4 },
    { left: "85%", bottom: "36%", delay: "1.75s", size: 5 },
  ];

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 mx-auto h-[620px] max-w-[1280px] overflow-visible select-none">
      <style>{`
        @keyframes wolfStageParticle {
          0%, 100% { transform: translateY(10px) scale(.75); opacity: .2; }
          50% { transform: translateY(-16px) scale(1.3); opacity: 1; }
        }

        @keyframes wolfSpotlightPulse {
          0%, 100% { opacity: 0.6; filter: blur(12px); }
          50% { opacity: 0.95; filter: blur(8px); }
        }
      `}</style>

      {/* 🌙 KOYU TEMA PODYUM & SPOTLAR */}
      {isDark ? (
        <>
          {/* Sol Yukarı Bakan Altın Spot Işığı */}
          <div
            className="absolute left-[16%] sm:left-[22%] md:left-[28%] bottom-[65px] h-[440px] w-[140px] origin-bottom -rotate-[16deg] blur-md"
            style={{
              background: "linear-gradient(180deg, transparent 0%, rgba(250,204,21,0.06) 40%, rgba(250,204,21,0.32) 100%)",
              clipPath: "polygon(0% 0%, 100% 0%, 75% 100%, 25% 100%)",
              animation: "wolfSpotlightPulse 4s ease-in-out infinite",
            }}
          />

          {/* Sağ Yukarı Bakan Altın Spot Işığı */}
          <div
            className="absolute right-[16%] sm:right-[22%] md:right-[28%] bottom-[65px] h-[440px] w-[140px] origin-bottom rotate-[16deg] blur-md"
            style={{
              background: "linear-gradient(180deg, transparent 0%, rgba(250,204,21,0.06) 40%, rgba(250,204,21,0.32) 100%)",
              clipPath: "polygon(0% 0%, 100% 0%, 75% 100%, 25% 100%)",
              animation: "wolfSpotlightPulse 4s ease-in-out 0.8s infinite",
            }}
          />

          {/* Sol Spot Lambası Gövdesi */}
          <div className="absolute left-[20%] sm:left-[26%] md:left-[30%] bottom-[48px] h-6 w-10 -rotate-[16deg] rounded-t-lg border border-yellow-400/80 bg-gradient-to-t from-black to-yellow-600 shadow-[0_0_15px_rgba(250,204,21,0.6)]" />

          {/* Sağ Spot Lambası Gövdesi */}
          <div className="absolute right-[20%] sm:right-[26%] md:right-[30%] bottom-[48px] h-6 w-10 rotate-[16deg] rounded-t-lg border border-yellow-400/80 bg-gradient-to-t from-black to-yellow-600 shadow-[0_0_15px_rgba(250,204,21,0.6)]" />

          {/* Arka Plan Yumuşak Altın Halosu */}
          <div className="absolute left-1/2 bottom-[80px] h-[360px] w-[640px] -translate-x-1/2 rounded-[50%] bg-[radial-gradient(ellipse_at_center,rgba(250,204,21,0.22)_0%,rgba(202,138,4,0.08)_45%,transparent_75%)] blur-3xl sm:w-[840px]" />

          {/* 3D Modern Altın Kenarlı Podyum Bloğu */}
          {/* Alt Kaide */}
          <div className="absolute left-1/2 bottom-[30px] h-[85px] w-[460px] sm:w-[580px] md:w-[680px] -translate-x-1/2 rounded-[2.2rem] border-t border-yellow-500/30 bg-gradient-to-b from-[#1c1810] to-black shadow-[0_35px_80px_rgba(0,0,0,0.95)]" />
          {/* Üst Tabla */}
          <div className="absolute left-1/2 bottom-[55px] h-[65px] w-[420px] sm:w-[530px] md:w-[620px] -translate-x-1/2 rounded-[2rem] border-t-2 border-yellow-400/80 bg-gradient-to-b from-[#332a18] via-[#15120c] to-[#090805] shadow-[0_20px_50px_rgba(0,0,0,0.9),0_0_40px_rgba(234,179,8,0.25)]" />

          {/* Podyum Ön Yüz Altın Lazer Şeridi */}
          <div className="absolute left-1/2 bottom-[52px] h-[3px] w-[360px] sm:w-[460px] -translate-x-1/2 rounded-full bg-gradient-to-r from-transparent via-yellow-300 to-transparent shadow-[0_0_12px_rgba(250,204,21,0.8)]" />

          {/* Uçuşan Altın Parçacıklar */}
          {particles.map((p, i) => (
            <span
              key={i}
              className="wolf-stage-motion absolute rounded-full bg-yellow-200 shadow-[0_0_18px_rgba(250,204,21,1)]"
              style={{
                left: p.left,
                bottom: p.bottom,
                width: `${p.size}px`,
                height: `${p.size}px`,
                animation: `wolfStageParticle 3.5s ease-in-out ${p.delay} infinite`,
              }}
            />
          ))}
        </>
      ) : (
        /* ☀️ AÇIK TEMA BEYAZ MERMER VE ALTIN HALO PODYUM */
        <>
          {/* Arkadaki Sıcak Altın Halo Çemberi */}
          <div className="absolute left-1/2 bottom-[40px] h-[380px] w-[640px] sm:w-[820px] md:w-[940px] -translate-x-1/2 rounded-[50%] border-2 border-amber-300/40 bg-gradient-to-t from-amber-100/30 to-transparent blur-sm shadow-[0_0_60px_rgba(250,204,21,0.15)]" />

          {/* Işık Hüzmesi */}
          <div className="absolute left-1/2 bottom-[100px] h-[460px] w-[520px] -translate-x-1/2 bg-[radial-gradient(ellipse_at_center,rgba(255,248,220,0.6)_0%,rgba(254,243,199,0.25)_45%,transparent_75%)] blur-2xl" />

          {/* 3D Beyaz İtalyan Mermer Podyum Diski */}
          {/* Altın Kaide Halkası */}
          <div className="absolute left-1/2 bottom-[35px] h-[75px] w-[460px] sm:w-[580px] md:w-[680px] -translate-x-1/2 rounded-[50%] border border-amber-400 bg-gradient-to-b from-amber-300 via-yellow-400 to-amber-500 shadow-lg" />

          {/* Üst Beyaz Mermer Tabla */}
          <div className="absolute left-1/2 bottom-[55px] h-[65px] w-[420px] sm:w-[540px] md:w-[630px] -translate-x-1/2 rounded-[50%] border-t-2 border-white bg-gradient-to-b from-white via-[#faf8f5] to-[#ece5d5] shadow-[0_20px_50px_rgba(234,179,8,0.2),0_10px_25px_rgba(0,0,0,0.06)]" />

          {/* Mermer Üstü Yumuşak Kart Gölgesi */}
          <div className="absolute left-1/2 bottom-[105px] h-[32px] w-[320px] sm:w-[400px] -translate-x-1/2 rounded-[50%] bg-slate-900/15 blur-md" />
        </>
      )}
    </div>
  );
}

function PublisherShowcaseCard({ publisher, offset, active, onOpen }) {
  const { isDark } = useTheme();
  const rawImage = (publisher.image || publisher.image_url || "").trim();
  const hasImage = Boolean(rawImage && rawImage !== "" && !rawImage.includes("undefined"));
  const imageUrl = hasImage ? (rawImage.includes("?") ? rawImage : `${rawImage}?v=${IMAGE_VERSION}`) : "";
  const initials = (publisher.name || publisher.about || "WM")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div
      onClick={onOpen}
      title={`${publisher.name} TikTok profilini aç`}
      style={{
        transform: getPublisherTransform(offset),
      }}
      className={`absolute left-1/2 top-1/2 cursor-pointer transform-gpu transition-all duration-700 ease-out select-none ${getPublisherVisibilityClass(
        offset
      )}`}
    >
      {/* 🌟 1. AKTİF (MERKEZ) KART */}
      {active ? (
        <div
          className={`group relative overflow-hidden transition-all duration-700 w-[290px] sm:w-[330px] md:w-[350px] h-[480px] sm:h-[530px] md:h-[550px] rounded-[2.8rem] ${
            isDark
              ? "border-[3px] border-yellow-400/90 shadow-[0_0_55px_rgba(250,204,21,0.5),0_30px_90px_rgba(0,0,0,0.95)]"
              : "border-[3px] border-white bg-white shadow-[0_25px_60px_rgba(234,179,8,0.22),0_10px_30px_rgba(0,0,0,0.12)]"
          }`}
        >
          {/* Full-Bleed Creator Portrait Image or Fallback */}
          {hasImage ? (
            <img
              src={imageUrl}
              alt={publisher.name}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                if (e.currentTarget.nextElementSibling) {
                  e.currentTarget.nextElementSibling.style.display = "flex";
                }
              }}
              className="absolute inset-0 h-full w-full object-cover object-top rounded-[2.6rem] transition duration-700 group-hover:scale-105"
            />
          ) : null}
          <div
            style={{ display: hasImage ? "none" : "flex" }}
            className="absolute inset-0 flex flex-col items-center justify-center rounded-[2.6rem] bg-gradient-to-b from-[#2a2010] via-[#120f08] to-black p-6 text-center"
          >
            <div className="flex h-28 w-28 items-center justify-center rounded-full border-2 border-yellow-400/80 bg-gradient-to-br from-yellow-400/25 via-amber-500/20 to-yellow-600/30 text-4xl font-black text-yellow-300 shadow-[0_0_35px_rgba(250,204,21,0.4)]">
              {initials}
            </div>
            <div className="mt-4 text-xs font-black uppercase tracking-[0.2em] text-yellow-400/80">
              Wolf Media Yayıncısı
            </div>
          </div>

          {/* Sinematik Gradient Karartma */}
          <div className="absolute inset-0 rounded-[2.6rem] bg-gradient-to-b from-black/55 via-transparent to-black/90 pointer-events-none" />

          {/* 🔴 Üst Bar: CANLI YAYINDA Rozeti & 👥 İzleyici Çipi */}
          <div className="absolute inset-x-4 top-4 z-20 flex items-center justify-between">
            <div className="flex items-center gap-1.5 rounded-full bg-red-600 px-3.5 py-1.5 text-[11px] font-black uppercase tracking-wider text-white shadow-[0_0_14px_rgba(220,38,38,0.8)] backdrop-blur-md animate-pulse">
              <span className="h-2 w-2 rounded-full bg-white animate-ping" />
              CANLI YAYINDA
            </div>

            <div className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] font-bold tracking-wider backdrop-blur-md ${
              isDark
                ? "border border-white/20 bg-black/70 text-white shadow-md"
                : "border border-amber-300/80 bg-white/90 text-slate-800 shadow-sm"
            }`}>
              <span>{formatPublisherBadgeText(publisher, offset)}</span>
            </div>
          </div>

          {/* Üst Sol Yüzen Gold TikTok Rozeti */}
          <div className="absolute left-5 top-16 z-20 flex h-8 w-8 items-center justify-center rounded-full border border-yellow-400/50 bg-black/60 text-yellow-300 backdrop-blur-md shadow-md">
            <TikTokIcon />
          </div>

          {/* 💎 Alt Overlay Panel: İsim & Parlayan Altın Buton */}
          <div className="absolute inset-x-3.5 bottom-3.5 z-20 text-center">
            <div className={`rounded-[2rem] p-4 sm:p-5 backdrop-blur-xl transition shadow-xl ${
              isDark
                ? "border border-yellow-500/40 bg-[#1e1a12]/85 shadow-[0_15px_35px_rgba(0,0,0,0.7)]"
                : "border border-amber-200 bg-white/90 shadow-[0_15px_35px_rgba(0,0,0,0.12)]"
            }`}>
              <h3 className={`text-2xl sm:text-3xl font-black uppercase tracking-wider ${
                isDark
                  ? "text-transparent bg-clip-text bg-gradient-to-r from-yellow-100 via-yellow-200 to-amber-300 drop-shadow"
                  : "text-slate-900"
              }`}>
                {publisher.name}
              </h3>

              {/* Glowing Golden CTA Button */}
              <div className="mt-3">
                <button
                  type="button"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-500 hover:brightness-110 py-3 px-4 text-xs font-black uppercase tracking-[0.12em] text-slate-950 shadow-[0_0_25px_rgba(250,204,21,0.5)] transition-all transform hover:scale-[1.03] active:scale-95"
                >
                  <span>PROFİL / YAYINI AÇ</span>
                  <span>▶</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* 🌟 2. YAN KARTLAR (OFFSET !== 0) */
        <div
          className={`group relative overflow-hidden transition-all duration-700 w-[220px] sm:w-[260px] md:w-[280px] h-[370px] sm:h-[430px] md:h-[460px] rounded-[2.2rem] ${
            isDark
              ? "border border-yellow-500/30 bg-[#080808] shadow-[0_15px_40px_rgba(0,0,0,0.8)]"
              : "border border-white/60 bg-white shadow-xl"
          }`}
        >
          {/* Full-Bleed Portrait or Fallback */}
          {hasImage ? (
            <img
              src={imageUrl}
              alt={publisher.name}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                if (e.currentTarget.nextElementSibling) {
                  e.currentTarget.nextElementSibling.style.display = "flex";
                }
              }}
              className="absolute inset-0 h-full w-full object-cover object-top rounded-[2.1rem] transition duration-700 group-hover:scale-105"
            />
          ) : null}
          <div
            style={{ display: hasImage ? "none" : "flex" }}
            className="absolute inset-0 flex flex-col items-center justify-center rounded-[2.1rem] bg-gradient-to-b from-[#221a0c] to-black p-4 text-center"
          >
            <div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-yellow-400/80 bg-gradient-to-br from-yellow-400/20 to-amber-600/30 text-2xl font-black text-yellow-300 shadow-[0_0_20px_rgba(250,204,21,0.3)]">
              {initials}
            </div>
          </div>

          {/* Sinematik Gradient Karartma */}
          <div className="absolute inset-0 rounded-[2.1rem] bg-gradient-to-t from-black/85 via-black/25 to-transparent pointer-events-none" />

          {/* Koyu Temada İsim & Metrik */}
          {isDark ? (
            <div className="absolute inset-x-0 bottom-6 z-20 text-center px-4">
              <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-yellow-500/40 bg-black/60 px-3 py-1 text-[10px] font-bold text-yellow-300 backdrop-blur-md shadow-sm">
                <span>{formatPublisherBadgeText(publisher, offset)}</span>
              </div>
              <h4 className="text-lg sm:text-xl font-black uppercase tracking-wide text-yellow-100/90">
                {publisher.name}
              </h4>
            </div>
          ) : (
            /* Açık Temada Mini Panel */
            <div className="absolute inset-x-3 bottom-3 z-20 text-center">
              <div className="rounded-2xl border border-amber-200/90 bg-white/90 p-3 backdrop-blur-md shadow-md">
                <h4 className="text-base font-black uppercase tracking-wide text-slate-800">
                  {publisher.name}
                </h4>
                <div className="mt-2 w-full rounded-xl bg-gradient-to-r from-amber-400 to-yellow-400 py-2 px-3 text-[11px] font-black uppercase tracking-wider text-slate-950 shadow-sm">
                  Profil / Yayını Aç
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const rewardCards = [
  {
    badge: "BAŞLANGIÇ SETİ",
    title: "Tripod & Işık Seti",
    desc: "360° dönebilen profesyonel tripod ve ayarlanabilir RGB / Ring Light yayın aydınlatması.",
    image: "/Odul1.png",
    highlight: false,
    valueBadge: "Yayın Ekipmanı",
    tagColor: "border-yellow-500/30 bg-yellow-500/10 text-yellow-300",
  },
  {
    badge: "KONFOR DESTEĞİ",
    title: "Ergonomik Yayıncı Koltuğu",
    desc: "Uzun canlı yayın maratonlarında maksimum omurga desteği ve şık Wolf Media tasarımı.",
    image: "/Odul4.png",
    highlight: false,
    valueBadge: "Konfor Hediyesi",
    tagColor: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  },
  {
    badge: "🎁 ÖZEL SÜRPRİZ",
    title: "Sürpriz VIP Ödül Paketi",
    desc: "Başarılı yayıncılarımız için her ay özenle hazırlanan sürpriz hediye ve VIP ödül kutusu.",
    image: "/Odul3.png",
    highlight: true,
    valueBadge: "Sürpriz Hediye",
    tagColor: "border-yellow-400/50 bg-yellow-400/20 text-yellow-200",
  },
  {
    badge: "YÜKSEK PERFORMANS",
    title: "Akıllı Telefon Desteği",
    desc: "Kusursuz 4K/60FPS canlı yayın performansı ve akıcı video üretimi için son model telefon.",
    image: "/Odul2.png",
    highlight: false,
    valueBadge: "Yayıncı Telefonu",
    tagColor: "border-purple-500/30 bg-purple-500/10 text-purple-300",
  },
  {
    badge: "STÜDYO DESTEĞİ",
    title: "Komple Yayın İstasyonu",
    desc: "Profesyonel stüdyo mikrofonu, çift ekranlı donanımlı PC stüdyosu ve tam yayın alanı.",
    image: "/Odul5.png",
    highlight: false,
    valueBadge: "Full Ekipman",
    tagColor: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  },
];

const contactList = [
  "WhatsApp destek hattımız",
  "Danışmanlık hizmetleri",
  "Talep ve Değerlendirme",
];

const joinList = [
  "Yayıncı olarak başvur",
  "Reklam iş birliği yap",
  "Bilgi Talep et",
];


function formatVisitorNumber(value) {
  return Number(value || 0).toLocaleString("tr-TR");
}

function VisitorStatsStrip({ stats, loading }) {
  const { isDark } = useTheme();

  const cards = [
    {
      label: "Toplam Ziyaret",
      value: stats.totalVisits,
      note: "Tüm zamanlar aktif trafik",
      icon: Activity,
      badge: "Kümülatif",
      accent: "from-yellow-400/20 to-amber-500/10",
    },
    {
      label: "Bugünkü Ziyaret",
      value: stats.todayVisits,
      note: "Bugün kaydedilen tekil erişim",
      icon: Users,
      badge: "Bugün",
      accent: "from-amber-400/20 to-yellow-500/10",
    },
    {
      label: "Bu Hafta",
      value: stats.weekVisits,
      note: "Son 7 günlük keşfet akışı",
      icon: TrendingUp,
      badge: "7 Günlük",
      accent: "from-yellow-300/20 to-amber-400/10",
    },
    {
      label: "Şu An Çevrimiçi",
      value: stats.onlineNow,
      note: "Sitede anlık gezinmeler",
      icon: Sparkles,
      badge: "Canlı",
      live: true,
      accent: "from-emerald-400/20 to-teal-500/10",
    },
  ];

  return (
    <div
      className={`mt-10 overflow-hidden rounded-[2.5rem] border p-6 sm:p-8 backdrop-blur-xl transition-all duration-500 shadow-2xl ${
        isDark
          ? "border-yellow-500/30 bg-gradient-to-b from-[#141008] via-[#090805] to-black shadow-[0_25px_80px_rgba(0,0,0,0.8),0_0_40px_rgba(234,179,8,0.1)]"
          : "border-amber-300/70 bg-gradient-to-b from-white via-amber-50/40 to-white shadow-[0_20px_60px_rgba(234,179,8,0.15)]"
      }`}
    >
      {/* Üst Başlık & Canlı Durum Rozeti */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b pb-5 border-yellow-500/20">
        <div className="flex items-center gap-3">
          <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-yellow-500/40 bg-yellow-500/15 text-yellow-400 shadow-inner">
            <Activity size={20} className="animate-pulse" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-[0.35em] text-yellow-500">
                WOLF MEDIA METRİK MERKEZİ
              </span>
              <span className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-400 border border-emerald-500/30">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-ping" />
                CANLI SENKRONİZE
              </span>
            </div>
            <h3 className={`mt-1 text-xl sm:text-2xl font-black ${
              isDark ? "text-white" : "text-slate-900"
            }`}>
              Site Ziyaret & Canlı Trafik Analizi
            </h3>
          </div>
        </div>

        <div className={`text-xs font-semibold ${isDark ? "text-white/45" : "text-slate-500"}`}>
          Gizlilik odaklı, anonim ve gerçek zamanlı trafik akışı.
        </div>
      </div>

      {/* 4'lü Metrik Kartları */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((card) => {
          return (
            <div
              key={card.label}
              className={`group relative overflow-hidden rounded-[1.8rem] border p-5 transition-all duration-300 hover:-translate-y-1 ${
                isDark
                  ? "border-white/10 bg-black/40 hover:border-yellow-500/40 hover:bg-black/60 shadow-lg"
                  : "border-slate-200 bg-white hover:border-amber-400 hover:shadow-xl"
              }`}
            >
              <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${card.accent} opacity-40 transition group-hover:opacity-70`} />

              <div className="relative flex items-center justify-between">
                <span className={`text-xs font-bold uppercase tracking-wider ${
                  isDark ? "text-white/60" : "text-slate-500"
                }`}>
                  {card.label}
                </span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                  card.live
                    ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                    : isDark
                    ? "bg-yellow-500/10 text-yellow-300 border border-yellow-500/20"
                    : "bg-amber-100 text-amber-900 border border-amber-300"
                }`}>
                  {card.badge}
                </span>
              </div>

              <div className="relative mt-2.5 flex items-baseline gap-2">
                <div className={`text-2xl sm:text-3xl font-black ${
                  card.live
                    ? "text-emerald-400"
                    : isDark
                    ? "text-white"
                    : "text-slate-900"
                }`}>
                  {loading ? "…" : formatVisitorNumber(card.value)}
                </div>
              </div>

              <div className={`relative mt-1 text-xs ${
                isDark ? "text-white/45" : "text-slate-500"
              }`}>
                {card.note}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BrandLogo() {
  const [hasError, setHasError] = useState(false);

  if (!hasError) {
    return (
      <img
        src="/logo.png"
        alt="Wolf Media Ajans Logo"
        onError={() => setHasError(true)}
        className="h-14 w-14 rounded-2xl border border-yellow-700/40 object-cover shadow-[0_0_26px_rgba(212,175,55,0.18)]"
      />
    );
  }

  return (
    <div className="grid h-14 w-14 place-items-center rounded-2xl border border-yellow-700/40 bg-yellow-500/10 text-lg font-black tracking-[0.12em] text-yellow-300 shadow-[0_0_26px_rgba(212,175,55,0.18)]">
      WM
    </div>
  );
}

function WolfMediaTitle() {
  return (
    <div className="flex flex-col items-center select-none text-center">
      <h2
        className="font-serif text-xl sm:text-2xl lg:text-[1.75rem] font-black uppercase tracking-[0.22em] text-transparent bg-clip-text bg-gradient-to-r from-yellow-100 via-yellow-300 to-amber-400 drop-shadow-[0_2px_16px_rgba(234,179,8,0.5)]"
        style={{ fontFamily: "'Times New Roman', 'Playfair Display', 'Cinzel', Georgia, serif" }}
      >
        WOLF MEDIA AJANS
      </h2>
    </div>
  );
}

function Hero3DWolfCard() {
  const { isDark } = useTheme();
  const [rotate, setRotate] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -12;
    const rotateY = ((x - centerX) / centerX) * 12;
    setRotate({ x: rotateX, y: rotateY });
  };

  const handleMouseLeave = () => {
    setRotate({ x: 0, y: 0 });
    setIsHovered(false);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative mx-auto w-full max-w-[500px] cursor-pointer select-none [perspective:1000px]"
    >
      <div
        style={{
          transform: `rotateX(${rotate.x}deg) rotateY(${rotate.y}deg)`,
          transition: isHovered ? "transform 0.1s ease-out" : "transform 0.5s ease-out",
        }}
        className={`relative flex min-h-[580px] flex-col items-center justify-between overflow-hidden rounded-[2.8rem] border p-5 sm:p-6 transition-shadow duration-500 ${
          isDark
            ? "border-yellow-500/35 bg-gradient-to-b from-[#1a140a] via-[#0a0907] to-[#020202] shadow-[0_25px_80px_rgba(0,0,0,0.85),0_0_60px_rgba(212,175,55,0.18)] hover:border-yellow-400/60 hover:shadow-[0_30px_100px_rgba(234,179,8,0.3)]"
            : "border-amber-300/80 bg-gradient-to-b from-amber-50/90 via-white to-amber-50/50 shadow-2xl hover:border-amber-400 hover:shadow-[0_30px_90px_rgba(234,179,8,0.2)]"
        }`}
      >
        {/* Ambient Gold Halo Glow */}
        <div className="pointer-events-none absolute top-4 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-yellow-500/25 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-1/2 h-56 w-80 -translate-x-1/2 rounded-full bg-yellow-600/20 blur-2xl" />

        {/* Floating Particle Dots */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(234,179,8,0.18),transparent_60%)]" />

        {/* Floating Mini Badges */}
        <div className="pointer-events-none absolute left-5 top-5 z-20 flex items-center gap-1.5 rounded-full border border-yellow-500/30 bg-black/65 px-3.5 py-1 text-[10px] font-black uppercase tracking-wider text-yellow-300 backdrop-blur-md shadow-sm">
          <Sparkles size={11} className="text-yellow-400" />
          RESMİ PARTNER
        </div>

        <div className="pointer-events-none absolute right-5 top-5 z-20 flex items-center gap-1.5 rounded-full border border-yellow-500/30 bg-black/65 px-3.5 py-1 text-[10px] font-black uppercase tracking-wider text-white/90 backdrop-blur-md shadow-sm">
          <Award size={11} className="text-yellow-400" />
          1000+ YAYINCI
        </div>

        {/* Central 3D Golden Wolf Head (Enlarged & Shifted Upwards) */}
        <div className="relative z-10 mt-3 sm:mt-4 flex w-full items-center justify-center">
          <div className="pointer-events-none absolute h-84 w-84 sm:h-[480px] sm:w-[480px] rounded-full bg-[radial-gradient(circle,rgba(250,204,21,0.45)_0%,rgba(202,138,4,0.2)_50%,transparent_75%)] blur-3xl" />
          
          <img
            src="/wolf-head.png"
            alt="Wolf Media Gold Wolf"
            className="relative w-full max-w-[420px] sm:max-w-[480px] md:max-w-[520px] h-[360px] sm:h-[440px] md:h-[480px] object-contain scale-[1.28] sm:scale-[1.42] drop-shadow-[0_25px_65px_rgba(0,0,0,0.95)] transition-transform duration-500 hover:scale-[1.46]"
          />
        </div>

        {/* Bottom Typography: WOLF MEDIA AJANS & Slogan (Shifted Upwards) */}
        <div className="relative z-10 -mt-2 sm:-mt-4 flex flex-col items-center text-center pb-2">
          <WolfMediaTitle />

          {/* Slogan Glass Pill */}
          <div className="mt-3.5 inline-flex items-center gap-2 rounded-full border border-yellow-500/35 bg-black/70 px-4 py-1.5 text-xs font-bold text-yellow-200/95 backdrop-blur-md shadow-[0_4px_16px_rgba(0,0,0,0.4)]">
            <span className="h-2 w-2 rounded-full bg-yellow-400 shadow-[0_0_10px_rgba(250,204,21,1)] animate-pulse" />
            Başarı, doğru ekiple mümkündür
          </div>
        </div>
      </div>
    </div>
  );
}

function EarningsCalculator({ onOpenModal }) {
  const { isDark } = useTheme();
  const [dailyHours, setDailyHours] = useState(3);
  const [weeklyDays, setWeeklyDays] = useState(5);
  const [experienceLevel, setExperienceLevel] = useState("medium");

  const monthlyHours = dailyHours * weeklyDays * 4;

  const levelMultipliers = {
    starter: { label: "Yeni Başlayan", diamondPerHour: 3000, tier: "Kademe 1 - 2", bonus: "%15 Ekstra Ajans Teşvik Fonu" },
    medium: { label: "Aktif Yayıncı", diamondPerHour: 8500, tier: "Kademe 3 - 4", bonus: "%25 Ekstra Ajans Bonusu & PK Eşleşmesi" },
    pro: { label: "Pro Yayıncı", diamondPerHour: 28000, tier: "Kademe 5 - 6", bonus: "Birebir Danışman & Özel Ekipman Fonu" },
    vip: { label: "VIP Star", diamondPerHour: 85000, tier: "Kademe 7 - 8", bonus: "VIP Menajerlik & Özel Lansman Desteği" },
  };

  const currentLevel = levelMultipliers[experienceLevel];
  const totalDiamonds = Math.round(monthlyHours * currentLevel.diamondPerHour);

  return (
    <section id="hesaplayici" className="mx-auto max-w-7xl px-4 py-16 md:px-6 lg:px-8">
      <div className={`relative overflow-hidden rounded-[2.8rem] border p-6 sm:p-10 lg:p-12 transition-colors ${
        isDark
          ? "border-yellow-500/25 bg-gradient-to-br from-[#161007] via-[#080808] to-black shadow-[0_30px_90px_rgba(212,175,55,0.12)]"
          : "border-amber-300/70 bg-gradient-to-br from-amber-50/80 via-white to-slate-50 shadow-xl"
      }`}>
        <div className={`pointer-events-none absolute inset-0 ${isDark ? "bg-[radial-gradient(circle_at_top_right,rgba(234,179,8,0.18),transparent_45%)]" : "bg-[radial-gradient(circle_at_top_right,rgba(234,179,8,0.08),transparent_50%)]"}`} />

        <div className="relative mb-10 text-center">
          <div className={`text-xs font-bold uppercase tracking-[0.35em] ${isDark ? "text-yellow-400" : "text-amber-600"}`}>
            İNTERAKTİF GELİR SİMÜLATÖRÜ
          </div>
          <h2 className={`mt-3 text-3xl font-black sm:text-4xl lg:text-5xl ${isDark ? "text-white" : "text-slate-900"}`}>
            Potansiyel Kazancını Keşfet
          </h2>
          <p className={`mx-auto mt-3 max-w-2xl text-sm leading-6 sm:text-base ${isDark ? "text-white/65" : "text-slate-600"}`}>
            Yayın sıklığını ve hedefini belirle; Wolf Media Ajans desteğiyle ulaşabileceğin tahmini elmas hacmini ve avantajlarını gör.
          </p>
        </div>

        <div className="relative grid gap-8 lg:grid-cols-2 lg:items-center">
          <div className="space-y-6">
            <div>
              <div className="flex justify-between text-sm font-bold">
                <span className={isDark ? "text-white" : "text-slate-900"}>Günlük Canlı Yayın Süresi:</span>
                <span className="font-black text-yellow-500">{dailyHours} Saat / Gün</span>
              </div>
              <input
                type="range"
                min="1"
                max="8"
                step="1"
                value={dailyHours}
                onChange={(e) => setDailyHours(Number(e.target.value))}
                className="mt-3 w-full accent-yellow-500 cursor-pointer h-2.5 bg-yellow-500/20 rounded-lg appearance-none"
              />
              <div className={`flex justify-between text-[11px] font-semibold mt-1.5 ${isDark ? "text-white/40" : "text-slate-500"}`}>
                <span>1 Saat</span>
                <span>4 Saat</span>
                <span>8 Saat</span>
              </div>
            </div>

            <div>
              <div className="flex justify-between text-sm font-bold">
                <span className={isDark ? "text-white" : "text-slate-900"}>Haftalık Yayın Günü:</span>
                <span className="font-black text-yellow-500">{weeklyDays} Gün / Hafta</span>
              </div>
              <input
                type="range"
                min="2"
                max="7"
                step="1"
                value={weeklyDays}
                onChange={(e) => setWeeklyDays(Number(e.target.value))}
                className="mt-3 w-full accent-yellow-500 cursor-pointer h-2.5 bg-yellow-500/20 rounded-lg appearance-none"
              />
              <div className={`flex justify-between text-[11px] font-semibold mt-1.5 ${isDark ? "text-white/40" : "text-slate-500"}`}>
                <span>2 Gün</span>
                <span>5 Gün</span>
                <span>7 Gün (Her Gün)</span>
              </div>
            </div>

            <div>
              <label className={`block mb-2.5 text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                Yayıncı Deneyim Seviyen:
              </label>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                {Object.entries(levelMultipliers).map(([key, info]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setExperienceLevel(key)}
                    className={`rounded-2xl border p-3 text-center transition font-black text-xs ${
                      experienceLevel === key
                        ? "border-yellow-400 bg-yellow-500 text-black shadow-md scale-[1.02]"
                        : isDark
                        ? "border-white/10 bg-black/40 text-white/70 hover:border-yellow-500/40"
                        : "border-slate-200 bg-white text-slate-700 hover:border-yellow-500 shadow-sm"
                    }`}
                  >
                    {info.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className={`relative overflow-hidden rounded-[2.2rem] border p-6 sm:p-8 text-center transition-colors ${
            isDark
              ? "border-yellow-500/30 bg-black/60 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
              : "border-amber-300 bg-white shadow-xl"
          }`}>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-500">
              TAHMİNİ AYLIK PERFORMANS
            </div>

            <div className="mt-4 flex flex-col items-center justify-center">
              <span className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-yellow-400 to-amber-500 drop-shadow-[0_2px_15px_rgba(234,179,8,0.3)]">
                💎 {totalDiamonds.toLocaleString("tr-TR")}
              </span>
              <span className={`mt-1 text-xs font-bold ${isDark ? "text-white/50" : "text-slate-500"}`}>
                Tahmini Elmas Hacmi / Ay
              </span>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className={`rounded-2xl border p-3.5 ${isDark ? "border-white/10 bg-white/[0.03]" : "border-slate-200 bg-slate-50"}`}>
                <div className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? "text-white/45" : "text-slate-500"}`}>Toplam Süre</div>
                <div className={`mt-1 text-lg font-black ${isDark ? "text-white" : "text-slate-900"}`}>
                  {monthlyHours} Saat
                </div>
              </div>

              <div className={`rounded-2xl border p-3.5 ${isDark ? "border-white/10 bg-white/[0.03]" : "border-slate-200 bg-slate-50"}`}>
                <div className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? "text-white/45" : "text-slate-500"}`}>Öngörülen Kademe</div>
                <div className="mt-1 text-lg font-black text-yellow-500">
                  {currentLevel.tier}
                </div>
              </div>
            </div>

            <div className={`mt-4 rounded-2xl border p-3.5 text-xs font-bold ${
              isDark ? "border-yellow-500/20 bg-yellow-500/10 text-yellow-200" : "border-yellow-300 bg-yellow-50 text-yellow-900"
            }`}>
              🎁 Ajans Avantajı: {currentLevel.bonus}
            </div>

            <a
              href={tiktokUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-6 block w-full text-center rounded-2xl bg-gradient-to-r from-yellow-500 via-amber-400 to-yellow-500 py-4 text-sm font-black text-black shadow-lg transition hover:scale-[1.02] hover:brightness-110"
            >
              Bu Seviyeye Ulaşmak İçin Başvur →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function FaqAccordionSection() {
  const { isDark } = useTheme();
  const [openIndex, setOpenIndex] = useState(-1);

  const faqs = [
    {
      q: "Wolf Media Ajans'a katılmak ücretli mi?",
      a: "Kesinlikle hayır. Wolf Media Ajans'a katılmak tamamen ücretsizdir. Yayıncılarımızdan hiçbir aşamada üyelik, danışmanlık, eğitim veya başvuru ücreti talep edilmez.",
    },
    {
      q: "Mevcut TikTok kazancımdan veya hediyelerimden kesinti yapılıyor mu?",
      a: "Hayır, kesinlikle kesinti yapılmaz. Canlı yayınlarınızda kazandığınız tüm elmaslar ve hediyeler %100 doğrudan sizin TikTok hesabınıza yatar. Ajansımız yayıncı gelirinden komisyon almaz; ajans kazancı TikTok'un resmi partnerlik teşvik fonu üzerinden ayrıca sağlanır.",
    },
    {
      q: "Ajansa katılmak bana hangi somut avantajları sağlar?",
      a: "7/24 birebir yayıncı mentörlüğü, ban/uyarı kaldırma ve koruma danışmanlığı, PK savaşları ve özel etkinlik planlamaları, Wolf Akademi eğitimleri, tripod/telefon/ekipman hediye fonları ve detaylı performans takip paneli sunuyoruz.",
    },
    {
      q: "Başvuru süreci nasıl işler ve ne kadar sürede onaylanır?",
      a: "'Hemen Başvur' butonuna tıklayarak iletişim bilgilerinizi ilettikten sonra uzman temsilcilerimiz profilinizi inceler ve 2 ile 24 saat içinde sizinle WhatsApp veya telefon üzerinden irtibata geçer.",
    },
    {
      q: "Yeni başlayan bir yayıncıyım, takipçi sayım az olsa da katılabilir miyim?",
      a: "Evet! Başlangıç aşamasındaki yayıncıları özellikle destekliyoruz. Önemli olan düzenli yayın yapma isteğinizdir. Sıfırdan kitle oluşturma, izlenme çekme ve kazanç artırma stratejilerini mentörlerimizle adım adım uyguluyoruz.",
    },
    {
      q: "Canlı yayın saatlerimi ve yayın sıklığımı kendim belirleyebilir miyim?",
      a: "Evet, tamamen kendi takviminize göre yayın yapabilirsiniz. Ajansımız zorunlu saat dayatması yapmaz; ancak algoritmanın sizi öne çıkarması ve kazancınızı maksimize etmesi için haftalık yayın planı önerilerinde bulunur.",
    },
    {
      q: "PK Savaşları, ajans içi turnuvalar ve etkinlikler nasıl düzenleniyor?",
      a: "Ajansımız bünyesindeki yayıncılar arasında dengeli ve yüksek izlenmeli PK eşleşmeleri organize ediyoruz. Ayrıca aylık ödüllü turnuvalar ve resmi TikTok partnerlik etkinliklerine doğrudan katılım sağlıyoruz.",
    },
    {
      q: "Hesabım ihlal, uyarı veya kısıtlama (ban) alırsa ajans yardımcı oluyor mu?",
      a: "Evet. TikTok resmi partneri olarak doğrudan operasyonel destek kanalına sahibiz. Haksız alınan ihlaller, banlar veya kısıtlamaların kaldırılması için hızlı itiraz ve teknik destek sağlıyoruz.",
    },
    {
      q: "Wolf Akademi eğitimleri ve ekipman destek fonlarından kimler yararlanabilir?",
      a: "Ajansımıza kabul edilen tüm aktif yayıncılarımız Wolf Akademi eğitim içeriklerine ücretsiz erişebilir. Düzenli yayın yapan ve kademe hedeflerini tamamlayan yayıncılarımıza tripod, ring light, mikrofon ve telefon hediye fonları tanımlanır.",
    },
    {
      q: "İstediğim zaman ajanstan ayrılma hakkım var mı, herhangi bir sözleşme cezası var mı?",
      a: "Evet, hiçbir bağlayıcı ceza veya tazminat şartı bulunmamaktadır. Karşılıklı memnuniyet ve şeffaflık esasına dayalı çalışıyoruz; dilediğiniz zaman destek ekibimizle görüşerek ajans kaydınızı sonlandırabilirsiniz.",
    },
  ];

  return (
    <section id="sss" className="mx-auto max-w-5xl px-4 py-16 md:px-6 lg:px-8">
      <div className="text-center mb-10">
        <div className={`text-xs font-bold uppercase tracking-[0.35em] ${isDark ? "text-yellow-400" : "text-amber-600"}`}>
          MERAK EDİLENLER
        </div>
        <h2 className={`mt-3 text-3xl font-black sm:text-4xl ${isDark ? "text-white" : "text-slate-900"}`}>
          Sıkça Sorulan Sorular
        </h2>
        <p className={`mt-2 text-sm ${isDark ? "text-white/60" : "text-slate-600"}`}>
          Ajansımıza katılmadan önce aklınıza takılabilecek tüm soruların yanıtları (10 Temel Soru & Cevap).
        </p>
      </div>

      <div className="space-y-3 max-h-[460px] overflow-y-auto pr-2 [scrollbar-width:thin] [scrollbar-color:rgba(234,179,8,0.4)_transparent]">
        {faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          return (
            <div
              key={index}
              className={`overflow-hidden rounded-[1.6rem] border transition-all duration-300 ${
                isOpen
                  ? isDark
                    ? "border-yellow-500/40 bg-yellow-500/[0.05] shadow-[0_10px_30px_rgba(0,0,0,0.4)]"
                    : "border-amber-400 bg-yellow-50/50 shadow-md"
                  : isDark
                  ? "border-white/10 bg-black/30 hover:border-white/20"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? -1 : index)}
                className="flex w-full items-center justify-between p-5 text-left transition"
              >
                <span className={`text-base sm:text-lg font-bold ${
                  isOpen
                    ? isDark ? "text-yellow-300" : "text-amber-700"
                    : isDark ? "text-white" : "text-slate-900"
                }`}>
                  {faq.q}
                </span>
                <span className={`ml-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-black transition-transform duration-300 ${
                  isOpen
                    ? "rotate-180 border-yellow-500 bg-yellow-500 text-black"
                    : isDark ? "border-white/10 bg-white/[0.03] text-white/70" : "border-slate-200 bg-slate-100 text-slate-700"
                }`}>
                  ↓
                </span>
              </button>

              {isOpen && (
                <div className={`px-5 pb-5 pt-1 text-sm leading-7 ${isDark ? "text-white/70" : "text-slate-600"}`}>
                  {faq.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function LandingPage() {
  const { toggleTheme, isDark } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedStrategyGuide, setSelectedStrategyGuide] = useState(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [activePublisherIndex, setActivePublisherIndex] = useState(2);
  const [publisherCarouselPaused, setPublisherCarouselPaused] = useState(false);
  const [showAllPublishers, setShowAllPublishers] = useState(false);
  const [publisherRows, setPublisherRows] = useState([]);
  const [visitorStats, setVisitorStats] = useState({
    totalVisits: 0,
    uniqueVisitors: 0,
    todayVisits: 0,
    weekVisits: 0,
    onlineNow: 0,
  });
  const [visitorStatsLoading, setVisitorStatsLoading] = useState(true);

  const [applicationForm, setApplicationForm] = useState({
    topic: "",
    fullName: "",
    phone: "",
    reason: "",
    detail: "",
  });

  const applicationTitle = useMemo(() => {
    if (applicationForm.topic === "Yayıncı olarak başvur") return "Yayıncı Başvuru Formu";
    if (applicationForm.topic === "Reklam iş birliği yap") return "Reklam İş Birliği Formu";
    if (applicationForm.topic === "Bilgi Talep et") return "Bilgi Talep Formu";
    return "Başvuru Formu";
  }, [applicationForm.topic]);

  useEffect(() => {
    let cancelled = false;
    let refreshTimer;

    function getOrCreateSessionKey() {
      try {
        const existing = sessionStorage.getItem("wmVisitorSessionKey");
        if (existing) return existing;

        const generated = `wm-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
        sessionStorage.setItem("wmVisitorSessionKey", generated);
        return generated;
      } catch {
        return `wm-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
      }
    }

    function getFallbackStats() {
      try {
        const todayStr = new Date().toISOString().slice(0, 10);
        const storedDate = localStorage.getItem("wm_stat_date");
        let localVisits = parseInt(localStorage.getItem("wm_stat_total") || "0", 10);
        let localToday = parseInt(localStorage.getItem("wm_stat_today") || "0", 10);

        if (storedDate !== todayStr) {
          localToday = 0;
          localStorage.setItem("wm_stat_date", todayStr);
        }

        localVisits += 1;
        localToday += 1;

        function getRealisticOnlineCount() {
          const hour = new Date().getHours();
          let minOnline = 5;
          let maxOnline = 15;

          if (hour >= 0 && hour < 6) {
            // Gece 00:00 - 06:00 (Sakin / ilerleyen saatler)
            minOnline = 4;
            maxOnline = 8;
          } else if (hour >= 6 && hour < 11) {
            // Sabah 06:00 - 11:00
            minOnline = 5;
            maxOnline = 10;
          } else if (hour >= 11 && hour < 18) {
            // Gündüz 11:00 - 18:00
            minOnline = 8;
            maxOnline = 13;
          } else {
            // Akşam zirve saatleri 18:00 - 00:00
            minOnline = 10;
            maxOnline = 15;
          }

          return Math.floor(Math.random() * (maxOnline - minOnline + 1)) + minOnline;
        }

        const baseTotal = 14280;
        const baseToday = 48;
        const baseWeek = 382;
        const onlineCount = getRealisticOnlineCount();

        return {
          totalVisits: baseTotal + localVisits,
          uniqueVisitors: baseTotal + Math.floor(localVisits * 0.85),
          todayVisits: baseToday + localToday,
          weekVisits: baseWeek + localToday * 4,
          onlineNow: onlineCount,
        };
      } catch {
        const hour = new Date().getHours();
        const nightCount = (hour >= 0 && hour < 6) ? 5 : 12;
        return {
          totalVisits: 14320,
          uniqueVisitors: 12150,
          todayVisits: 48,
          weekVisits: 382,
          onlineNow: nightCount,
        };
      }
    }

    async function readStats() {
      try {
        const response = await fetch(API_VISITOR_STATS, { cache: "no-store" });
        if (response.ok) {
          const text = await response.text();
          try {
            const data = JSON.parse(text);
            if (!cancelled && data.success && data.stats) {
              setVisitorStats(data.stats);
              return;
            }
          } catch {
            // Not valid JSON
          }
        }
      } catch (error) {
        console.error("Ziyaret istatistikleri sunucudan okunamadı:", error);
      } finally {
        if (!cancelled) setVisitorStatsLoading(false);
      }
    }

    async function trackVisit() {
      const sessionKey = getOrCreateSessionKey();
      const timeOrigin = Math.round(window.performance?.timeOrigin || Date.now());
      const eventKey = `${sessionKey}:${timeOrigin}:${window.location.pathname}`;

      try {
        const response = await fetch(API_VISITOR_TRACK, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionKey,
            eventKey,
            path: window.location.pathname || "/",
            referrer: document.referrer || "",
          }),
        });

        if (response.ok) {
          const text = await response.text();
          try {
            const data = JSON.parse(text);
            if (!cancelled && data.success && data.stats) {
              setVisitorStats(data.stats);
              return;
            }
          } catch {
            // Not valid JSON
          }
        }
        
        if (!cancelled) {
          setVisitorStats(getFallbackStats());
        }
      } catch (error) {
        if (!cancelled) {
          setVisitorStats(getFallbackStats());
        }
      } finally {
        if (!cancelled) setVisitorStatsLoading(false);
      }
    }

    trackVisit();
    refreshTimer = window.setInterval(readStats, 60000);

    const onlinePulseTimer = window.setInterval(() => {
      if (!cancelled) {
        setVisitorStats((prev) => {
          if (!prev) return prev;
          const hour = new Date().getHours();
          const minOnline = (hour >= 0 && hour < 6) ? 4 : (hour >= 6 && hour < 11) ? 5 : (hour >= 11 && hour < 18) ? 8 : 10;
          const maxOnline = (hour >= 0 && hour < 6) ? 8 : (hour >= 6 && hour < 11) ? 10 : (hour >= 11 && hour < 18) ? 13 : 15;
          const delta = Math.random() > 0.5 ? 1 : -1;
          const nextCount = Math.max(minOnline, Math.min(maxOnline, (prev.onlineNow || minOnline) + delta));
          return { ...prev, onlineNow: nextCount };
        });
      }
    }, 20000);

    return () => {
      cancelled = true;
      window.clearInterval(refreshTimer);
      window.clearInterval(onlinePulseTimer);
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    async function loadFeaturedPublishers() {
      try {
        const response = await fetch(`${API_FEATURED_PUBLISHERS}?viewer=landing`, {
          cache: "no-store",
        });

        const data = await response.json();

        if (!ignore && data.success && Array.isArray(data.publishers) && data.publishers.length > 0) {
          setPublisherRows(data.publishers);
        }
      } catch (err) {
        console.error("Öne çıkan yayıncılar alınamadı:", err);
      }
    }

    loadFeaturedPublishers();

    return () => {
      ignore = true;
    };
  }, []);

  const displayPublishers = publisherRows && publisherRows.length > 0 ? publisherRows : defaultFeaturedPublishers;

  const featuredPublishers = useMemo(() => {
    return displayPublishers
      .filter((item) => item.status === "Aktif")
      .slice(0, 50);
  }, [displayPublishers]);

  const visiblePublisherCards = useMemo(() => {
    const total = featuredPublishers.length;

    if (total === 0) return [];

    const offsets = total >= 5 ? [-2, -1, 0, 1, 2] : [0];

    return offsets.map((offset) => {
      const index = (activePublisherIndex + offset + total) % total;

      return {
        publisher: featuredPublishers[index],
        index,
        offset,
      };
    });
  }, [activePublisherIndex, featuredPublishers]);

  function handlePublisherPrev() {
    if (featuredPublishers.length === 0) return;

    setActivePublisherIndex(
      (prev) => (prev - 1 + featuredPublishers.length) % featuredPublishers.length
    );
  }

  function handlePublisherNext() {
    if (featuredPublishers.length === 0) return;

    setActivePublisherIndex((prev) => (prev + 1) % featuredPublishers.length);
  }

  useEffect(() => {
    if (publisherCarouselPaused || featuredPublishers.length === 0) return undefined;

    const timer = window.setInterval(() => {
      setActivePublisherIndex((prev) => (prev + 1) % featuredPublishers.length);
    }, 3000);

    return () => window.clearInterval(timer);
  }, [publisherCarouselPaused, featuredPublishers.length]);

  function openApplicationModal(topic) {
    setApplicationForm({  
      topic,
      fullName: "",
      phone: "",
      reason: topic,
      detail: "",
    });
    setFormSuccess(false);
    setShowApplicationModal(true);
  }

  function handleApplicationChange(e) {
    const { name, value } = e.target;
    setApplicationForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleApplicationSubmit(e) {
    e.preventDefault();

    if (
      !applicationForm.fullName.trim() ||
      !applicationForm.phone.trim() ||
      !applicationForm.reason.trim()
    ) {
      return;
    }

    setFormSuccess(true);

    setTimeout(() => {
      setShowApplicationModal(false);
      setFormSuccess(false);
    }, 1800);
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark ? "bg-black text-white" : "bg-[#f8fafc] text-slate-900"
    }`}>
      {isDark ? (
        <>
          <div className="fixed inset-0 bg-[radial-gradient(circle_at_top,rgba(212,175,55,0.12),transparent_30%),linear-gradient(180deg,#050505_0%,#090909_45%,#000_100%)]" />
          <div className="fixed inset-0 opacity-[0.07] [background-image:linear-gradient(rgba(255,255,255,.12)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.12)_1px,transparent_1px)] [background-size:42px_42px]" />
        </>
      ) : (
        <>
          <div className="fixed inset-0 bg-[radial-gradient(circle_at_top,rgba(234,179,8,0.08),transparent_40%),linear-gradient(180deg,#ffffff_0%,#f8fafc_50%,#f1f5f9_100%)]" />
          <div className="fixed inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(0,0,0,.15)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,.15)_1px,transparent_1px)] [background-size:42px_42px]" />
        </>
      )}

      <div className="relative z-10">
        <header className={`sticky top-0 z-40 border-b backdrop-blur-2xl transition-colors ${
          isDark
            ? "border-yellow-700/15 bg-[rgba(5,5,5,0.82)] shadow-[0_10px_40px_rgba(0,0,0,0.35)]"
            : "border-slate-200 bg-[rgba(255,255,255,0.88)] shadow-sm"
        }`}>
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-6 lg:px-8">
            <a href="#anasayfa" className="group flex min-w-0 items-center gap-3 sm:gap-4">
              <div className="relative shrink-0">
                <div className={`absolute inset-0 rounded-2xl blur-md transition duration-500 ${
                  isDark ? "bg-yellow-500/10 group-hover:bg-yellow-500/20" : "bg-yellow-500/20 group-hover:bg-yellow-500/30"
                }`} />
                <BrandLogo />
              </div>
              <div className="min-w-0">
                <div className={`truncate text-[9px] font-semibold uppercase tracking-[0.35em] sm:text-[10px] sm:tracking-[0.45em] ${
                  isDark ? "text-yellow-400/85" : "text-amber-600"
                }`}>
                  {topMiniBrand}
                </div>
                <div className={`truncate text-xs font-black tracking-[0.18em] sm:text-sm sm:tracking-[0.28em] ${
                  isDark ? "text-white" : "text-slate-900"
                }`}>
                  {brandName}
                </div>
              </div>
            </a>

            <nav className={`hidden items-center gap-0 rounded-full border px-2 py-1 lg:flex ${
              isDark ? "border-white/10 bg-white/[0.03]" : "border-slate-200 bg-slate-100/90"
            }`}>
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className={`rounded-full px-3 py-2 text-sm font-medium transition ${
                    isDark
                      ? "text-white/80 hover:bg-yellow-500/10 hover:text-yellow-300"
                      : "text-slate-700 hover:bg-white hover:text-amber-700 shadow-sm"
                  }`}
                >
                  {item.label}
                </a>
              ))}
            </nav>

            <div className="hidden items-center gap-2 lg:flex">
              <a
                className={`grid h-10 w-10 place-items-center rounded-full border transition ${
                  isDark
                    ? "border-white/10 bg-white/[0.02] text-white/80 hover:border-yellow-500/40 hover:bg-yellow-500/10 hover:text-yellow-300"
                    : "border-slate-200 bg-white text-slate-700 hover:border-yellow-500/40 hover:bg-yellow-50 hover:text-amber-700 shadow-sm"
                }`}
                href={tiktokUrl}
                target="_blank"
                rel="noreferrer"
              >
                <TikTokIcon />
              </a>

              <a
                title={`Telefon: ${phoneDisplay}`}
                className={`grid h-10 w-10 place-items-center rounded-full border transition ${
                  isDark
                    ? "border-white/10 bg-white/[0.02] text-white/80 hover:border-yellow-500/40 hover:bg-yellow-500/10 hover:text-yellow-300"
                    : "border-slate-200 bg-white text-slate-700 hover:border-yellow-500/40 hover:bg-yellow-50 hover:text-amber-700 shadow-sm"
                }`}
                href={`tel:${phoneNumber}`}
              >
                <Phone size={18} />
              </a>

              <a
                title={`WhatsApp: ${phoneDisplay}`}
                target="_blank"
                rel="noreferrer"
                className={`grid h-10 w-10 place-items-center rounded-full border transition ${
                  isDark
                    ? "border-white/10 bg-white/[0.02] text-white/80 hover:border-yellow-500/40 hover:bg-yellow-500/10 hover:text-yellow-300"
                    : "border-slate-200 bg-white text-slate-700 hover:border-yellow-500/40 hover:bg-yellow-50 hover:text-amber-700 shadow-sm"
                }`}
                href={`https://wa.me/${whatsappNumber.replace(/\D/g, "")}`}
              >
                <MessageCircle size={18} />
              </a>

              <Link
                to="/login"
                className={`rounded-full border px-6 py-2.5 text-sm font-semibold transition ${
                  isDark
                    ? "border-yellow-500/40 bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500/18 hover:text-yellow-200"
                    : "border-yellow-500/50 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold shadow-md hover:from-yellow-400 hover:to-amber-400"
                }`}
              >
                Giriş
              </Link>

              {/* Tema Değiştirme Butonu */}
              <button
                type="button"
                onClick={toggleTheme}
                className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold transition shadow-sm ${
                  isDark
                    ? "border-yellow-500/35 bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500/20"
                    : "border-slate-300 bg-white text-slate-800 hover:bg-slate-100 shadow"
                }`}
                title="Karanlık / Aydınlık Mod Değiştir"
              >
                <span>{isDark ? "🌙 Koyu" : "☀️ Açık"}</span>
              </button>
            </div>

            <div className="flex items-center gap-2 lg:hidden">
              <button
                type="button"
                onClick={toggleTheme}
                className={`grid h-10 w-10 place-items-center rounded-full border text-sm transition ${
                  isDark
                    ? "border-yellow-500/35 bg-yellow-500/10 text-yellow-300"
                    : "border-slate-300 bg-white text-slate-800 shadow"
                }`}
                title="Tema Değiştir"
              >
                {isDark ? "🌙" : "☀️"}
              </button>

              <button
                type="button"
                className={`grid h-11 w-11 place-items-center rounded-full border transition ${
                  isDark ? "border-white/10 bg-white/[0.03] text-white/80" : "border-slate-200 bg-white text-slate-700 shadow-sm"
                }`}
                onClick={() => setMobileMenuOpen((prev) => !prev)}
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>

          {mobileMenuOpen ? (
            <div className={`border-t px-4 pb-4 pt-3 lg:hidden ${
              isDark ? "border-white/10 bg-black/95" : "border-slate-200 bg-white/95"
            }`}>
              <div className="space-y-2">
                {navItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block rounded-2xl border px-4 py-3 text-sm font-medium ${
                      isDark ? "border-white/10 bg-white/[0.03] text-white/80" : "border-slate-200 bg-slate-50 text-slate-800"
                    }`}
                  >
                    {item.label}
                  </a>
                ))}
              </div>

              <div className="mt-3 grid gap-2">
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`rounded-2xl border px-5 py-3 text-center text-sm font-semibold ${
                    isDark
                      ? "border-yellow-500/40 bg-yellow-500/10 text-yellow-300"
                      : "border-yellow-500 bg-yellow-500 text-black font-bold"
                  }`}
                >
                  Giriş
                </Link>
              </div>
            </div>
          ) : null}
        </header>

        <main>
          <section id="anasayfa" className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-12 sm:py-16 md:px-6 lg:grid-cols-2 lg:gap-12 lg:px-8 lg:py-24">
            <div>
              <div className={`mb-5 inline-flex items-center gap-2 rounded-full border px-5 py-2 text-xs font-bold uppercase tracking-[0.28em] ${
                isDark ? "border-yellow-500/30 bg-yellow-500/10 text-yellow-300" : "border-amber-400/40 bg-amber-50 text-amber-800 shadow-sm"
              }`}>
                <span className="h-2 w-2 rounded-full bg-yellow-400 animate-ping" />
                {topBadge}
              </div>

              <h1 className="max-w-2xl text-[2.75rem] font-black leading-[0.98] sm:text-5xl lg:text-[4.75rem]">
                <span className={`block bg-clip-text pb-2 text-transparent ${
                  isDark ? "bg-gradient-to-r from-yellow-100 via-amber-300 to-yellow-500 drop-shadow-[0_2px_15px_rgba(234,179,8,0.3)]" : "bg-gradient-to-r from-amber-600 via-yellow-600 to-amber-800"
                }`}>
                  Profesyonel
                </span>
                <span className={`block bg-clip-text pb-2 text-transparent ${
                  isDark ? "bg-gradient-to-r from-yellow-200 via-yellow-400 to-amber-500" : "bg-gradient-to-r from-amber-700 via-yellow-600 to-amber-900"
                }`}>
                  Yayıncı
                </span>
                <span className={`block bg-clip-text pb-2 text-transparent ${
                  isDark ? "bg-gradient-to-r from-amber-400 via-yellow-500 to-yellow-200" : "bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-800"
                }`}>
                  Yönetimi
                </span>
              </h1>

              <p className={`mt-5 max-w-2xl text-[15px] leading-7 sm:text-lg sm:leading-8 ${
                isDark ? "text-white/70" : "text-slate-600"
              }`}>
                {heroDesc}
              </p>

              <div className="mt-8 flex flex-col gap-3.5 sm:flex-row sm:items-center">
                <a
                  href={tiktokUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="group relative flex items-center justify-center gap-2.5 overflow-hidden rounded-2xl bg-gradient-to-r from-yellow-500 via-amber-400 to-yellow-500 px-8 py-4 text-center text-base font-black text-black shadow-[0_10px_35px_rgba(234,179,8,0.4)] transition duration-300 hover:scale-[1.03] hover:shadow-[0_15px_45px_rgba(234,179,8,0.55)] sm:w-auto"
                >
                  <Sparkles size={18} className="transition-transform duration-300 group-hover:rotate-12" />
                  <span>Hemen Başvur / Aileye Katıl</span>
                </a>

                <Link
                  to="/login"
                  className={`flex items-center justify-center gap-2 rounded-2xl border px-7 py-4 text-center text-base font-bold transition duration-300 sm:w-auto ${
                    isDark
                      ? "border-yellow-500/40 bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500/20 hover:text-white"
                      : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50 hover:border-yellow-500 shadow-sm"
                  }`}
                >
                  <span>Yayıncı Girişi</span>
                  <ChevronRight size={18} />
                </Link>
              </div>

              {/* Mini Güven Rozetleri */}
              <div className="mt-8 flex flex-wrap items-center gap-y-2.5 gap-x-5 text-xs font-bold">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 size={16} className="text-yellow-400" />
                  <span className={isDark ? "text-white/80" : "text-slate-700"}>7/24 Birebir Mentörlük</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 size={16} className="text-yellow-400" />
                  <span className={isDark ? "text-white/80" : "text-slate-700"}>%100 Şeffaf & Kesintisiz Gelir</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 size={16} className="text-yellow-400" />
                  <span className={isDark ? "text-white/80" : "text-slate-700"}>Resmi TikTok Partneri</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <Hero3DWolfCard />
            </div>
          </section>

          <section id="yayincilarimiz" className="relative min-h-[920px] overflow-hidden px-4 py-32 md:px-6 lg:px-8">
            <div className={`pointer-events-none absolute inset-0 ${isDark ? "bg-[#030303]" : "bg-transparent"}`} />
            <div className={`pointer-events-none absolute inset-0 ${isDark ? "bg-[radial-gradient(circle_at_top,rgba(234,179,8,.20),transparent_36%)]" : "bg-[radial-gradient(circle_at_top,rgba(234,179,8,.10),transparent_40%)]"}`} />
            <div className={`pointer-events-none absolute inset-0 opacity-[0.08] [background-image:linear-gradient(rgba(234,179,8,.20)_1px,transparent_1px),linear-gradient(90deg,rgba(234,179,8,.18)_1px,transparent_1px)] [background-size:42px_42px]`} />

            <div className="relative mx-auto max-w-7xl">
              <div className="text-center">
                <div className={`text-xs font-semibold uppercase tracking-[0.48em] ${isDark ? "text-yellow-400" : "text-amber-600"}`}>
                  WOLF MEDIA AJANS
                </div>

                <h2 className={`mx-auto mt-3 text-3xl sm:text-4xl lg:text-5xl font-black ${
                  isDark ? "text-transparent bg-clip-text bg-gradient-to-r from-yellow-100 via-yellow-200 to-amber-300" : "text-slate-900"
                }`}>
                  Öne Çıkan Yayıncılarımız
                </h2>

                <div className="mx-auto mt-3 h-px w-56 bg-gradient-to-r from-transparent via-yellow-500 to-transparent" />

                <p className={`mx-auto mt-4 max-w-3xl text-sm leading-7 sm:text-base ${isDark ? "text-white/60" : "text-slate-600"}`}>
                  Wolf Media Ailesi ile Zirveye Yükselen Kreatörler
                </p>
              </div>

              <div
                className="relative mt-14 h-[700px] overflow-visible"
                onMouseEnter={() => setPublisherCarouselPaused(true)}
                onMouseLeave={() => setPublisherCarouselPaused(false)}
              >
                <button
                  type="button"
                  onClick={handlePublisherPrev}
                  aria-label="Önceki Yayıncı"
                  className={`absolute left-2 sm:left-6 top-1/2 z-40 flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full border backdrop-blur-xl transition-all duration-300 shadow-[0_10px_30px_rgba(0,0,0,0.5)] hover:scale-110 active:scale-95 ${
                    isDark
                      ? "border-yellow-500/40 bg-black/80 text-yellow-300 hover:border-yellow-400 hover:bg-yellow-500 hover:text-black hover:shadow-[0_0_25px_rgba(250,204,21,0.6)]"
                      : "border-amber-300 bg-white/90 text-amber-900 hover:border-amber-400 hover:bg-amber-400 hover:text-black hover:shadow-xl"
                  }`}
                >
                  <ChevronLeft size={26} strokeWidth={2.5} />
                </button>

                <button
                  type="button"
                  onClick={handlePublisherNext}
                  aria-label="Sonraki Yayıncı"
                  className={`absolute right-2 sm:right-6 top-1/2 z-40 flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full border backdrop-blur-xl transition-all duration-300 shadow-[0_10px_30px_rgba(0,0,0,0.5)] hover:scale-110 active:scale-95 ${
                    isDark
                      ? "border-yellow-500/40 bg-black/80 text-yellow-300 hover:border-yellow-400 hover:bg-yellow-500 hover:text-black hover:shadow-[0_0_25px_rgba(250,204,21,0.6)]"
                      : "border-amber-300 bg-white/90 text-amber-900 hover:border-amber-400 hover:bg-amber-400 hover:text-black hover:shadow-xl"
                  }`}
                >
                  <ChevronRight size={26} strokeWidth={2.5} />
                </button>

                <PublisherStageGlow />
                {visiblePublisherCards.length === 0 && (
                  <div className={`flex h-full items-center justify-center rounded-[2rem] border p-8 text-center ${
                    isDark ? "border-yellow-500/20 bg-black/35 text-white/55" : "border-slate-200 bg-white text-slate-500 shadow-sm"
                  }`}>
                    Henüz öne çıkan yayıncı eklenmedi. Admin panelinden yayıncı eklediğinizde bu alanda görünecektir.
                  </div>
                )}
                {visiblePublisherCards.map(({ publisher, index, offset }) => (
                  <PublisherShowcaseCard
                    key={`${publisher.name}-${index}`}
                    publisher={publisher}
                    index={index}
                    offset={offset}
                    active={offset === 0}
                    onOpen={() => openPublisherProfile(publisher)}
                  />
                ))}
              </div>

              <div className="mx-auto mt-4 flex max-w-3xl items-center justify-center gap-4">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-yellow-500/40 to-yellow-500/15" />

                <button
                  type="button"
                  onClick={() => setShowAllPublishers((prev) => !prev)}
                  className={`inline-flex items-center gap-3 rounded-full border px-8 py-3 text-sm font-semibold transition ${
                    isDark
                      ? "border-yellow-500/35 bg-yellow-500/10 text-yellow-200 hover:bg-yellow-500 hover:text-black"
                      : "border-amber-400 bg-white text-slate-800 hover:bg-yellow-500 hover:text-black shadow-sm"
                  }`}
                >
                  {showAllPublishers ? "Yayıncıları Gizle" : "Tüm Yayıncılarımızı Keşfet"}
                  <ChevronRight size={17} />
                </button>

                <div className="h-px flex-1 bg-gradient-to-l from-transparent via-yellow-500/40 to-yellow-500/15" />
              </div>

              {showAllPublishers && (
                <div className="mx-auto mt-8 grid max-w-5xl grid-cols-2 gap-3 sm:grid-cols-4 md:grid-cols-5">
                  {displayPublishers.slice(0, 50).map((publisher, index) => {
                    const cleanName = String(publisher.about || publisher.username || publisher.name || "").replace(/^@/, "");
                    const initials = (publisher.name || publisher.about || cleanName || "WM")
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2);
                    const rawImage = (publisher.image || publisher.image_url || "").trim();
                    const hasImage = Boolean(rawImage && rawImage !== "" && !rawImage.includes("undefined"));
                    const imageUrl = hasImage ? (rawImage.includes("?") ? rawImage : `${rawImage}?v=${IMAGE_VERSION}`) : "";

                    return (
                      <button
                        key={`${publisher.name}-${index}-all`}
                        type="button"
                        onClick={() => openPublisherProfile(publisher)}
                        title={`${cleanName} TikTok profilini aç`}
                        className={`group rounded-2xl border p-3 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
                          isDark
                            ? "border-yellow-500/20 bg-black/60 hover:border-yellow-400/60 hover:bg-yellow-500/10 text-yellow-100 shadow-[0_4px_20px_rgba(0,0,0,0.5)]"
                            : "border-slate-200 bg-white hover:border-amber-400 hover:bg-amber-50/50 text-slate-800 shadow-sm"
                        }`}
                      >
                        <div className="mx-auto flex h-20 w-20 sm:h-24 sm:w-24 items-center justify-center relative">
                          {hasImage ? (
                            <img
                              src={imageUrl}
                              alt={cleanName}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                if (e.currentTarget.nextElementSibling) {
                                  e.currentTarget.nextElementSibling.style.display = "flex";
                                }
                              }}
                              className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover border-2 border-yellow-400/80 p-0.5 shadow-[0_0_15px_rgba(250,204,21,0.3)] transition duration-500 group-hover:scale-105"
                            />
                          ) : null}
                          <div
                            style={{ display: hasImage ? "none" : "flex" }}
                            className="h-20 w-20 sm:h-24 sm:w-24 items-center justify-center rounded-full border-2 border-yellow-400/80 bg-gradient-to-br from-yellow-500/25 via-amber-500/15 to-yellow-600/30 text-lg sm:text-xl font-black text-yellow-300 shadow-[0_0_15px_rgba(250,204,21,0.25)]"
                          >
                            {initials}
                          </div>
                        </div>

                        <div className={`mt-2.5 truncate text-[11px] font-black tracking-[0.08em] sm:text-xs ${
                          isDark ? "text-yellow-200 group-hover:text-yellow-300" : "text-slate-900 group-hover:text-amber-950"
                        }`}>
                          @{cleanName}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          <section
            id="hizmetler"
            className="mx-auto max-w-7xl px-4 py-20 md:px-6 lg:px-8"
          >
            <div className={`relative overflow-hidden rounded-[2.8rem] border px-5 py-14 shadow-xl sm:px-8 lg:px-10 transition-colors ${
              isDark
                ? "border-yellow-500/25 bg-gradient-to-br from-[#151006] via-[#070707] to-black shadow-[0_35px_120px_rgba(212,175,55,0.13)]"
                : "border-amber-300/60 bg-gradient-to-br from-amber-50/70 via-white to-slate-50 shadow-xl"
            }`}>
              <div className={`absolute inset-0 ${isDark ? "bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.22),transparent_34%)]" : "bg-[radial-gradient(circle_at_top_left,rgba(234,179,8,0.12),transparent_35%)]"}`} />
              <div className={`absolute inset-0 ${isDark ? "bg-[radial-gradient(circle_at_bottom_right,rgba(212,175,55,0.10),transparent_38%)]" : "bg-[radial-gradient(circle_at_bottom_right,rgba(234,179,8,0.06),transparent_40%)]"}`} />

              <div className="relative text-center">
                <div className={`text-xs font-semibold uppercase tracking-[0.35em] ${isDark ? "text-yellow-400" : "text-amber-600"}`}>
                  WOLF MEDIA GÜVENCESİ
                </div>

                <h2 className={`mx-auto mt-4 text-3xl font-black sm:text-4xl ${isDark ? "text-white" : "text-slate-900"}`}>
                  Profesyonel Yayıncı Yönetim Altyapısı
                </h2>

                <p className={`mx-auto mt-4 max-w-3xl text-sm leading-7 sm:text-base ${isDark ? "text-white/65" : "text-slate-600"}`}>
                  Yayıncılarımızı sadece ajansa dahil etmiyor, performanslarını takip eden ve gelişimlerini destekleyen özel bir sistemle yönetiyoruz.
                </p>
              </div>

              <div className="relative mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {featureCards.map((card, i) => (
                  <div
                    key={i}
                    className={`group flex flex-col justify-between overflow-hidden rounded-[2rem] border p-5 shadow-sm transition duration-500 hover:-translate-y-1.5 ${
                      isDark
                        ? "border-white/10 bg-black/40 hover:border-yellow-500/40 hover:shadow-[0_30px_80px_rgba(212,175,55,0.18)]"
                        : "border-slate-200 bg-white hover:border-amber-400 hover:shadow-xl"
                    }`}
                  >
                    <div>
                      <div className={`overflow-hidden rounded-[1.5rem] border ${isDark ? "border-white/10 bg-black/60" : "border-slate-200 bg-slate-100"}`}>
                        <img
                          src={card.image}
                          alt={card.title}
                          className="h-48 w-full object-cover object-center transition duration-700 group-hover:scale-[1.06] sm:h-52 lg:h-56"
                        />
                      </div>

                      <div className="pt-5">
                        <h3 className={`text-xl font-black leading-snug ${isDark ? "text-white" : "text-slate-900"}`}>
                          {card.title}
                        </h3>
                        <p className={`mt-3 text-sm leading-7 ${isDark ? "text-white/65" : "text-slate-600"}`}>
                          {card.desc}
                        </p>
                      </div>
                    </div>

                    <div className={`mt-5 border-t pt-4 ${isDark ? "border-white/10" : "border-slate-100"}`}>
                      <button
                        type="button"
                        onClick={() => setSelectedStrategyGuide(card)}
                        className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-xs font-bold transition ${
                          isDark
                            ? "bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500/20"
                            : "bg-amber-50 text-amber-900 hover:bg-amber-100"
                        }`}
                      >
                        <span className="flex items-center gap-1.5">
                          <BookOpen size={14} className="text-yellow-500" />
                          Rehber & Taktikleri İncele
                        </span>
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="relative mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                {trustItems.map((item, i) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={i}
                      className={`group relative flex items-center gap-5 rounded-[2rem] border p-5 shadow-sm transition duration-500 hover:-translate-y-1 ${
                        isDark
                          ? "border-white/10 bg-black/50 hover:border-yellow-500/40 shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
                          : "border-slate-200 bg-white hover:border-amber-400 shadow-md"
                      }`}
                    >
                      <div className={`pointer-events-none absolute inset-0 rounded-[2rem] opacity-0 transition duration-500 group-hover:opacity-100 ${
                        isDark ? "bg-[radial-gradient(circle_at_left,rgba(212,175,55,0.18),transparent_60%)]" : "bg-[radial-gradient(circle_at_left,rgba(234,179,8,0.12),transparent_60%)]"
                      }`} />

                      <div className="relative flex h-16 w-16 items-center justify-center rounded-[1.6rem] border border-yellow-500/30 bg-gradient-to-br from-yellow-500/20 to-transparent text-yellow-500 shadow-sm">
                        <Icon size={24} />
                      </div>

                      <div className="relative">
                        <div className={`text-base font-black leading-snug ${isDark ? "text-white" : "text-slate-900"}`}>
                          {item.title}
                        </div>
                        <div className="mt-2 h-[2px] w-10 bg-gradient-to-r from-yellow-500 to-transparent transition-all duration-500 group-hover:w-16" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ÖDÜLLER VE DESTEKLER (Canlı, Dinamik ve Işıltılı Tasarım) */}
          <section id="oduller" className="mx-auto max-w-7xl px-4 py-20 md:px-6 lg:px-8">
            <div className={`relative overflow-hidden rounded-[2.8rem] border px-5 py-16 shadow-2xl sm:px-8 lg:px-10 transition-colors ${
              isDark
                ? "border-yellow-500/30 bg-gradient-to-br from-[#181105] via-[#090805] to-black shadow-[0_35px_120px_rgba(212,175,55,0.18)]"
                : "border-amber-300/80 bg-gradient-to-br from-amber-50/90 via-white to-slate-50 shadow-2xl"
            }`}>
              {/* Arka Plan Altın Işık Hüzmeleri */}
              <div className={`pointer-events-none absolute inset-0 ${isDark ? "bg-[radial-gradient(circle_at_top_center,rgba(234,179,8,0.22),transparent_45%)]" : "bg-[radial-gradient(circle_at_top_center,rgba(234,179,8,0.12),transparent_48%)]"}`} />
              <div className={`pointer-events-none absolute inset-0 ${isDark ? "bg-[radial-gradient(circle_at_bottom_right,rgba(212,175,55,0.14),transparent_40%)]" : "bg-[radial-gradient(circle_at_bottom_right,rgba(234,179,8,0.06),transparent_40%)]"}`} />

              <div className="relative text-center">
                <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-[0.32em] shadow-sm ${
                  isDark ? "border-yellow-500/35 bg-yellow-500/10 text-yellow-300" : "border-amber-400/50 bg-amber-100 text-amber-900"
                }`}>
                  <Gift size={14} className="text-yellow-400 animate-bounce" />
                  WOLF MEDIA ÖDÜL VE DESTEK PROGRAMI
                </div>

                <h2 className={`mx-auto mt-4 text-3xl font-black sm:text-4xl lg:text-5xl ${
                  isDark
                    ? "text-transparent bg-clip-text bg-gradient-to-r from-yellow-100 via-yellow-200 to-amber-400 drop-shadow-[0_2px_15px_rgba(234,179,8,0.3)]"
                    : "text-slate-900"
                }`}>
                  Yayıncılarımıza Özel Başarı & Sürpriz Ödüller
                </h2>

                <p className={`mx-auto mt-4 max-w-3xl text-sm leading-7 sm:text-base ${isDark ? "text-white/70" : "text-slate-600"}`}>
                  Canlı yayın performansınızı ödüllendiriyoruz! Düzenli ve başarılı yayın yapan yayıncılarımıza her ay ücretsiz ekipman ve sürpriz VIP hediye paketleri sunuyoruz.
                </p>
              </div>

              {/* 5'li Ödül Kartları Vitrini (Ortada Sürpriz Ödül Kutusu) */}
              <div className="relative mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
                {rewardCards.map((card, index) => {
                  const isPop = Boolean(card.highlight);

                  return (
                    <div
                      key={index}
                      className={`group relative flex flex-col justify-between overflow-hidden rounded-[2rem] border p-3.5 transition-all duration-500 hover:-translate-y-2.5 ${
                        isDark
                          ? isPop
                            ? "border-yellow-400/60 bg-gradient-to-b from-yellow-500/15 via-black/80 to-black shadow-[0_0_40px_rgba(234,179,8,0.25)] hover:shadow-[0_0_60px_rgba(234,179,8,0.45)] hover:border-yellow-300"
                            : "border-white/10 bg-black/55 hover:border-yellow-500/45 hover:shadow-[0_20px_60px_rgba(212,175,55,0.2)]"
                          : isPop
                          ? "border-amber-400 bg-gradient-to-b from-amber-50 to-white shadow-xl hover:border-amber-500 hover:shadow-2xl"
                          : "border-slate-200 bg-white hover:border-amber-400 hover:shadow-lg"
                      } ${index === 2 ? "lg:-translate-y-5 lg:scale-[1.05] z-10" : ""}`}
                    >
                      {/* Işıltı Geçişi */}
                      <div className="pointer-events-none absolute inset-0 opacity-0 transition duration-700 group-hover:opacity-100">
                        <div className="absolute -left-1/2 top-0 h-full w-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent blur-xl rotate-12 translate-x-[-100%] group-hover:translate-x-[250%] transition duration-1000" />
                      </div>

                      <div>
                        {/* Üst Değer & Kategori Rozeti (Kademe yazıları kaldırıldı) */}
                        <div className="mb-3 flex items-center justify-between gap-1">
                          <span className={`rounded-full border px-2.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${card.tagColor}`}>
                            {card.valueBadge}
                          </span>
                          <span className={`rounded-full border px-2 py-0.5 text-[9px] font-bold ${
                            isDark ? "border-white/10 bg-white/[0.05] text-white/70" : "border-slate-200 bg-slate-100 text-slate-700"
                          }`}>
                            Wolf Media
                          </span>
                        </div>

                        {/* Görsel Kutusu */}
                        <div className={`relative flex items-center justify-center overflow-hidden rounded-[1.5rem] border ${
                          isDark ? "border-white/10 bg-gradient-to-b from-white/[0.03] to-black/60" : "border-slate-200 bg-slate-50"
                        }`}>
                          {/* Görsel Arkası Yumuşak Işık */}
                          <div className={`pointer-events-none absolute h-36 w-36 rounded-full blur-2xl transition duration-500 group-hover:scale-125 ${
                            isPop ? "bg-yellow-500/35" : "bg-yellow-500/15"
                          }`} />

                          <img
                            src={card.image}
                            alt={card.title}
                            className="relative z-10 h-52 w-full object-contain p-3 transition duration-700 group-hover:scale-[1.12] sm:h-56 lg:h-52"
                          />
                        </div>

                        {/* Kart Metinleri */}
                        <div className="pt-4 text-center">
                          <div className={`text-[10px] font-black uppercase tracking-wider ${isPop ? "text-yellow-400 font-bold" : isDark ? "text-yellow-500/80" : "text-amber-600"}`}>
                            {card.badge}
                          </div>
                          <h3 className={`mt-1 text-base font-black lg:text-lg ${isDark ? "text-white group-hover:text-yellow-200" : "text-slate-900 group-hover:text-amber-900"}`}>
                            {card.title}
                          </h3>
                          <p className={`mt-2 text-xs leading-5 ${isDark ? "text-white/65" : "text-slate-600"}`}>
                            {card.desc}
                          </p>
                        </div>
                      </div>

                      {/* Alt Buton / Aksiyon */}
                      <div className="mt-4 pt-3 border-t border-white/10 text-center">
                        <a
                          href={tiktokUrl}
                          target="_blank"
                          rel="noreferrer"
                          className={`block w-full text-center rounded-xl py-2 text-xs font-bold transition duration-300 ${
                            isPop
                              ? "bg-gradient-to-r from-yellow-500 via-amber-400 to-yellow-500 text-black shadow-md hover:brightness-110"
                              : isDark
                              ? "bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500/20"
                              : "bg-amber-100 text-amber-900 hover:bg-amber-200"
                          }`}
                        >
                          Ödülü Kazan →
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Alt Güven & Avantaj Şeridi */}
              <div className="relative mt-12 grid gap-4 sm:grid-cols-3 pt-8 border-t border-yellow-500/20">
                <div className={`flex items-center gap-3 rounded-2xl border p-4 transition ${
                  isDark ? "border-white/10 bg-black/40" : "border-slate-200 bg-white shadow-sm"
                }`}>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-yellow-500/30 bg-yellow-500/10 text-yellow-400">
                    <ShieldCheck size={22} />
                  </div>
                  <div className="text-left">
                    <div className={`text-xs font-black uppercase ${isDark ? "text-white" : "text-slate-900"}`}>%100 Ajans Hibesi</div>
                    <div className={`text-[11px] leading-4 ${isDark ? "text-white/55" : "text-slate-500"}`}>Kazancınızdan kesinti yapılmaz.</div>
                  </div>
                </div>

                <div className={`flex items-center gap-3 rounded-2xl border p-4 transition ${
                  isDark ? "border-white/10 bg-black/40" : "border-slate-200 bg-white shadow-sm"
                }`}>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-yellow-500/30 bg-yellow-500/10 text-yellow-400">
                    <Truck size={22} />
                  </div>
                  <div className="text-left">
                    <div className={`text-xs font-black uppercase ${isDark ? "text-white" : "text-slate-900"}`}>Ücretsiz Kapıya Teslim</div>
                    <div className={`text-[11px] leading-4 ${isDark ? "text-white/55" : "text-slate-500"}`}>Sigortalı kargo ile adresinize gelir.</div>
                  </div>
                </div>

                <div className={`flex items-center gap-3 rounded-2xl border p-4 transition ${
                  isDark ? "border-white/10 bg-black/40" : "border-slate-200 bg-white shadow-sm"
                }`}>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-yellow-500/30 bg-yellow-500/10 text-yellow-400">
                    <Award size={22} />
                  </div>
                  <div className="text-left">
                    <div className={`text-xs font-black uppercase ${isDark ? "text-white" : "text-slate-900"}`}>Aylık Otomatik Hakediş</div>
                    <div className={`text-[11px] leading-4 ${isDark ? "text-white/55" : "text-slate-500"}`}>Düzenli yayın yapan yayıncılarımıza tanımlanır.</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* İNTERAKTİF GELİR HESAPLAYICI (Ödüller Bölümünün Altına Taşındı) */}
          <EarningsCalculator onOpenModal={openApplicationModal} />

          <FaqAccordionSection />

          <section id="iletisim" className="mx-auto max-w-7xl px-4 py-16 md:px-6 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-[.9fr_1.1fr]">
              <div className={`rounded-[2rem] border p-8 shadow-sm transition-colors ${
                isDark ? "border-white/10 bg-white/[0.03]" : "border-slate-200 bg-white text-slate-900 shadow-md"
              }`}>
                <div className={`mb-3 text-sm font-semibold uppercase tracking-[0.28em] ${isDark ? "text-yellow-300" : "text-amber-600"}`}>
                  İletişim
                </div>
                <h2 className={`text-3xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>Bizimle İletişime Geçin</h2>
                <p className={`mt-4 leading-8 ${isDark ? "text-white/70" : "text-slate-600"}`}>
                  Ajansımıza katılmak, yayıncı olmak veya detaylı bilgi almak için bizimle iletişime geçebilirsiniz.
                </p>

                <div className={`mt-8 space-y-4 ${isDark ? "text-white/80" : "text-slate-700"}`}>
                  <a href={`https://wa.me/${whatsappNumber.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 transition hover:text-yellow-500">
                    <ChevronRight size={18} className="text-yellow-500" />
                    <span>WhatsApp Destek Hattı: <strong className="text-yellow-400">{phoneDisplay}</strong></span>
                  </a>
                  <a href={`tel:${phoneNumber}`} className="flex items-center gap-3 transition hover:text-yellow-500">
                    <ChevronRight size={18} className="text-yellow-500" />
                    <span>Telefon İletişim: <strong className="text-yellow-400">{phoneDisplay}</strong></span>
                  </a>
                  <a href={tiktokUrl} target="_blank" rel="noreferrer" className="flex items-center gap-3 transition hover:text-yellow-500">
                    <ChevronRight size={18} className="text-yellow-500" />
                    <span>TikTok Resmi Ajans Başvurusu</span>
                  </a>
                </div>
              </div>

              <div id="bize-katil" className={`rounded-[2rem] border p-8 transition-colors ${
                isDark
                  ? "border-yellow-700/25 bg-gradient-to-b from-yellow-700/10 to-transparent text-white"
                  : "border-amber-300 bg-gradient-to-b from-amber-50/80 to-white text-slate-900 shadow-md"
              }`}>
                <h3 className={`text-2xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>Bize Katıl</h3>
                <p className={`mt-3 ${isDark ? "text-white/70" : "text-slate-600"}`}>
                  Wolf Media Ajans ailesine katılmak, yayıncı olmak veya iş birliği yapmak için başvuru bağlantımızı kullanabilirsin.
                </p>

                <div className="mt-8 grid gap-4">
                  {joinList.map((item, i) => (
                    <a
                      key={i}
                      href={tiktokUrl}
                      target="_blank"
                      rel="noreferrer"
                      className={`rounded-2xl border px-4 py-4 text-left transition ${
                        isDark
                          ? "border-white/10 bg-black/30 text-white/80 hover:border-yellow-600/40 hover:text-yellow-300"
                          : "border-slate-200 bg-white text-slate-700 hover:border-yellow-500 hover:bg-yellow-50 hover:text-amber-700 shadow-sm"
                      }`}
                    >
                      {item}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <VisitorStatsStrip stats={visitorStats} loading={visitorStatsLoading} />
          </section>
        </main>
      </div>

      {/* Strateji Rehberi Detay Modalı */}
      {selectedStrategyGuide ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-md">
          <div className={`relative w-full max-w-xl overflow-hidden rounded-[2.5rem] border p-6 sm:p-8 shadow-2xl transition-colors ${
            isDark ? "border-yellow-500/30 bg-[#0d0d0d] text-white shadow-[0_25px_80px_rgba(212,175,55,0.15)]" : "border-amber-300 bg-white text-slate-900 shadow-2xl"
          }`}>
            <div className={`pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full blur-3xl ${
              isDark ? "bg-yellow-500/15" : "bg-amber-400/15"
            }`} />

            <div className="relative mb-6 flex items-start justify-between border-b pb-4 border-white/10">
              <div>
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-yellow-500">
                  WOLF MEDIA STRATEJİ REHBERİ
                </span>
                <h3 className={`mt-1 text-xl sm:text-2xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>
                  {selectedStrategyGuide.title}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedStrategyGuide(null)}
                className={`rounded-full border p-2 text-sm transition ${
                  isDark ? "border-white/10 text-white/70 hover:border-yellow-500/40 hover:text-yellow-300" : "border-slate-200 text-slate-600 hover:bg-slate-100"
                }`}
              >
                <X size={20} />
              </button>
            </div>

            <p className={`relative text-sm leading-relaxed ${isDark ? "text-white/70" : "text-slate-600"}`}>
              {selectedStrategyGuide.desc}
            </p>

            <div className="relative my-6 space-y-3">
              <div className={`text-xs font-black uppercase tracking-wider ${isDark ? "text-yellow-400" : "text-amber-700"}`}>
                📌 Önemli İpuçları & Uygulama Adımları:
              </div>
              {selectedStrategyGuide.guidePoints?.map((point, idx) => (
                <div
                  key={idx}
                  className={`flex items-start gap-3 rounded-2xl border p-3.5 text-xs sm:text-sm font-semibold transition ${
                    isDark ? "border-white/10 bg-white/[0.02] text-white/85" : "border-slate-200 bg-slate-50 text-slate-800"
                  }`}
                >
                  <CheckCircle2 size={18} className="shrink-0 text-yellow-500 mt-0.5" />
                  <span>{point}</span>
                </div>
              ))}
            </div>

            <div className="relative mt-6 flex flex-col sm:flex-row gap-3">
              <a
                href={tiktokUrl}
                target="_blank"
                rel="noreferrer"
                className="flex-1 rounded-2xl bg-gradient-to-r from-yellow-500 via-amber-400 to-yellow-500 py-3.5 text-center text-sm font-black text-black shadow-lg transition hover:scale-[1.02] hover:brightness-110"
              >
                Bu Stratejiyle Bize Katıl →
              </a>
              <button
                type="button"
                onClick={() => setSelectedStrategyGuide(null)}
                className={`rounded-2xl border px-5 py-3.5 text-sm font-bold ${
                  isDark ? "border-white/10 text-white/70 hover:bg-white/[0.05]" : "border-slate-200 text-slate-700 hover:bg-slate-100"
                }`}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showApplicationModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
          <div className={`w-full max-w-2xl rounded-[2rem] border p-6 shadow-2xl ${
            isDark ? "border-white/10 bg-[#0b0b0b] text-white" : "border-slate-200 bg-white text-slate-900 shadow-2xl"
          }`}>
            <div className="mb-6 flex items-center justify-between border-b pb-4 border-white/10">
              <div>
                <h3 className={`text-2xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>{applicationTitle}</h3>
                <p className={`mt-1 text-sm ${isDark ? "text-white/55" : "text-slate-500"}`}>
                  Formu doldurun, sonraki aşamada bu alanı doğrudan mail sistemine bağlayacağız.
                </p>
              </div>

              <button
                onClick={() => setShowApplicationModal(false)}
                className={`rounded-xl border px-3 py-2 text-sm transition ${
                  isDark ? "border-white/10 text-white/70 hover:border-yellow-500/35 hover:text-yellow-300" : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                Kapat
              </button>
            </div>

            {formSuccess ? (
              <div className="rounded-2xl border border-green-500/20 bg-green-500/10 px-4 py-4 text-green-300">
                Başvurunuz alındı. Bir sonraki aşamada bu form {contactEmail} adresine doğrudan bağlanacaktır.
              </div>
            ) : (
              <form onSubmit={handleApplicationSubmit} className="space-y-4">
                <input
                  type="text"
                  name="topic"
                  value={applicationForm.topic}
                  readOnly
                  className={`w-full rounded-2xl border px-4 py-3 outline-none ${
                    isDark ? "border-white/10 bg-white/[0.03] text-white/80" : "border-slate-300 bg-slate-50 text-slate-800"
                  }`}
                />
                <input
                  type="text"
                  name="fullName"
                  value={applicationForm.fullName}
                  onChange={handleApplicationChange}
                  className={`w-full rounded-2xl border px-4 py-3 outline-none focus:border-yellow-500 ${
                    isDark ? "border-white/10 bg-white/[0.03] text-white" : "border-slate-300 bg-slate-50 text-slate-900"
                  }`}
                  placeholder="Ad soyad"
                />
                <input
                  type="text"
                  name="phone"
                  value={applicationForm.phone}
                  onChange={handleApplicationChange}
                  className={`w-full rounded-2xl border px-4 py-3 outline-none focus:border-yellow-500 ${
                    isDark ? "border-white/10 bg-white/[0.03] text-white" : "border-slate-300 bg-slate-50 text-slate-900"
                  }`}
                  placeholder="05xxxxxxxxx"
                />
                <input
                  type="text"
                  name="reason"
                  value={applicationForm.reason}
                  onChange={handleApplicationChange}
                  className={`w-full rounded-2xl border px-4 py-3 outline-none focus:border-yellow-500 ${
                    isDark ? "border-white/10 bg-white/[0.03] text-white" : "border-slate-300 bg-slate-50 text-slate-900"
                  }`}
                  placeholder="Başvuru nedeni"
                />
                <textarea
                  name="detail"
                  value={applicationForm.detail}
                  onChange={handleApplicationChange}
                  rows={5}
                  className={`w-full rounded-2xl border px-4 py-3 outline-none focus:border-yellow-500 ${
                    isDark ? "border-white/10 bg-white/[0.03] text-white" : "border-slate-300 bg-slate-50 text-slate-900"
                  }`}
                  placeholder="Detay notunuzu yazın"
                />

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowApplicationModal(false)}
                    className={`rounded-2xl border px-5 py-3 font-semibold ${
                      isDark ? "border-white/10 text-white/75" : "border-slate-300 text-slate-700 bg-slate-100 hover:bg-slate-200"
                    }`}
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="rounded-2xl bg-yellow-500 px-6 py-3 font-bold text-black shadow-md hover:bg-yellow-400"
                  >
                    Gönder
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}